import React, { useEffect, useRef, useState } from "react";
import {
    BookingBlock,
    Button,
    Flexbox,
    RoomCol,
    TimeCell,
    WrapperBox,
} from "../Theme/GlobalStyles";
import { globalTheme } from "../Theme/Theme";
import { Text } from "../Theme/Typography";
import { AngleDown, Clock, Users } from "../Theme/Icons";
import { BookingDetailsBody } from "./Popup/BookingDetailsPopup";
import { CancelBookingBody } from "./Popup/CancelBookingPopup";
import { PopupBody, PopupWrapper } from "./Popup/CommonPopup";
import CreateBookingPopup from "./Popup/CreateBookingPopup";
import { toast } from "react-toastify";
import moment from "moment";
import { canonicalSchoolId, schoolNameForId } from "../Constants/schools";
import { slotRunsFor } from "../../utils/util";

const HOURS = Array.from({ length: 17 }, (_, i) => 7 + i); // 7 → 23
const START_HOUR = 7;
const END_HOUR = START_HOUR + HOURS.length; // 24 (right edge of the grid, i.e. midnight)
const SNAP = 0.25; // 15-minute drag/selection snapping (Google-Calendar-style)
const LEFT_COL_WIDTH = 200; // px — frozen room column
const HOUR_COL_PX = 100; // width of one hour column (Day / expanded-day view)
const WEEK_COL_PX = 150; // width of one weekday column (Week view)
const DASH_PENDING = "#E8A23D"; // amber dashed border for "Under Review" slots
// Fixed row heights so the frozen left pane stays aligned with the scrolling right pane.
const ROW_H = 64;
// How close to the bottom of the grid (px) before the next page of rooms loads,
// and how many placeholder rows each loading state shows.
const ROOM_SCROLL_THRESHOLD = 200;
const INITIAL_SKELETON_ROWS = 6;
const MORE_SKELETON_ROWS = 3;
const SEC_H = 41;
const STRIP_H = 46;
const HDR_H = 35;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// "Pending" requests an admin still needs to action.
const isPendingStatus = (s) => s === "Pending" || s === "Under Review";

const formatHour = (h) => `${String(h).padStart(2, "0")}:00`;

const pad2 = (n) => String(n).padStart(2, "0");
// 24h label that supports quarter hours (e.g. 9.25 → "09:15").
const formatTime = (h) => `${pad2(Math.floor(h))}:${pad2(Math.round((h % 1) * 60))}`;
// 12h label that supports quarter hours (e.g. 13.25 → "1:15 PM").
const formatTime12 = (h) => {
    const hh = Math.floor(h);
    const mm = Math.round((h % 1) * 60);
    const ampm = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${pad2(mm)} ${ampm}`;
};
const formatDuration = (start, end) => {
    const mins = Math.round((end - start) * 60);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h} hour${h === 1 ? "" : "s"}`;
    return `${m} min`;
};
const toIsoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromIsoDate = (iso) => new Date(`${iso}T00:00:00`);
const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
};
const startOfWeek = (d) => {
    const x = new Date(d);
    const day = x.getDay(); // 0 Sun ... 6 Sat
    const diff = day === 0 ? -6 : 1 - day; // Monday-start
    x.setDate(x.getDate() + diff);
    x.setHours(0, 0, 0, 0);
    return x;
};
const formatLongDate = (d) =>
    d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const formatDayHeader = (d) =>
    d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const formatPopupDate = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
};
// Fractional hour → "HH:mm" for the booking-request slot strings.
const fracToHHMM = (h) => `${pad2(Math.floor(h))}:${pad2(Math.round((h % 1) * 60))}`;

// Nobody — admin included — books a slot that has already finished. The grid
// renders times in UTC (see fracOf), so compare against "now" in UTC too,
// otherwise the cut-off drifts by the viewer's offset.
const isPastSlot = (dateIso, endFrac) => {
    if (!dateIso) return false;
    const now = moment.utc();
    const end = moment.utc(dateIso, "YYYY-MM-DD")
        .add(Math.floor(endFrac), "hours")
        .add(Math.round((endFrac % 1) * 60), "minutes");
    return end.isBefore(now);
};
// A whole day is unbookable once its last slot has passed.
const isPastDay = (dateIso) => isPastSlot(dateIso, END_HOUR);
// Earliest fractional hour still bookable on `dateIso`: the whole grid for a
// future day, nothing at all for a past one, and "from now" for today.
const bookableFloor = (dateIso) => {
    if (!dateIso) return START_HOUR;
    const now = moment.utc();
    const day = moment.utc(dateIso, "YYYY-MM-DD");
    if (day.isAfter(now, "day")) return START_HOUR;
    if (day.isBefore(now, "day")) return END_HOUR;
    return clamp(now.hour() + now.minute() / 60, START_HOUR, END_HOUR);
};

// Faint 15-minute gridlines inside each hour cell, so the quarter-hour
// boundaries the admin is selecting are visible (Day / expanded-day). A 1px
// line repeats every 25% of the hour cell = every 15 minutes.
const QUARTER_LINES =
    "repeating-linear-gradient(to right, #f1f1f1 0 1px, transparent 1px 25%)";

// Flat wash for slots that have already passed — unbookable, but the bookings
// drawn on top of them stay readable and copyable.
const PAST_CELL_BG = "#F4F5F7";

// ---- Map the live availableClassroomsList into chart shape ----
const PALETTE = ["green", "yellow", "red", "blue", "purple"];
const hashStr = (s) => {
    let h = 0;
    const str = String(s);
    for (let i = 0; i < str.length; i++) {
        h = (h << 5) - h + str.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h);
};
const colorFor = (key) => PALETTE[hashStr(key) % PALETTE.length];

// Vibrant, easy-to-distinguish palette used to colour bookings BY SCHOOL, so
// every booking for the same school shares one hue. Each entry is a full
// {bg, bd, tx} triple so both the Day blocks and Week capsules can use it
// directly (no named-key indirection). Keyed on a stable school identifier.
export const SCHOOL_PALETTE = [
    { bg: "#E7F6EC", bd: "#2FB86A", tx: "#1E7E45" }, // green
    { bg: "#E3F7F4", bd: "#12B3A3", tx: "#0E7A6E" }, // teal
    { bg: "#E7F0FF", bd: "#3B7DF0", tx: "#2F62C4" }, // blue
    { bg: "#EAE7FB", bd: "#6C5CE7", tx: "#4B3FBF" }, // indigo
    { bg: "#F3EAFB", bd: "#9B4FE0", tx: "#6B3FBF" }, // purple
    { bg: "#FDE8F1", bd: "#E5478F", tx: "#B83271" }, // pink
    { bg: "#FDE9E9", bd: "#F0554A", tx: "#C0392B" }, // red
    { bg: "#FFEEDD", bd: "#F0832F", tx: "#B9611B" }, // orange
    { bg: "#FEF5D9", bd: "#E8B517", tx: "#8A6E0F" }, // amber
    { bg: "#EEF7DA", bd: "#7CB518", tx: "#537A0F" }, // lime
];
// Stable key per school so the same school always maps to the same colour.
// Resolve a real identifier in priority order:
//   • school_id         — FK on both confirmed bookings and requests
//   • school.id / name  — nested school object on confirmed bookings
//                         (attributes: id, school_name), NOT a plain string
//   • School            — free-text school name on requests
//   • school_name       — flat name fallback
// The nested `school` object must be unwrapped: hashing the object itself
// stringifies to "[object Object]" for every row, collapsing all schools to one
// colour — which is the bug this replaces. The result is then canonicalised, so
// a booking that stored "PGP" and one that stored the PGP FK land on one key.
export const schoolKeyOf = (obj) =>
    canonicalSchoolId(
        obj?.school_id ||
        obj?.school?.id ||
        obj?.school?.school_name ||
        obj?.School ||
        obj?.school_name ||
        "—"
    );
// Fallback colour when the admin hasn't picked one for the school yet: a pure
// function of the key, so an untouched school is still stable and distinct.
export const schoolColorForKey = (key) =>
    SCHOOL_PALETTE[hashStr(canonicalSchoolId(key ?? "—")) % SCHOOL_PALETTE.length];
export const schoolColor = (obj) => schoolColorForKey(schoolKeyOf(obj));
// Human-readable school name for legends/tooltips (the key may be an opaque FK).
export const schoolLabelOf = (obj) =>
    schoolNameForId(schoolKeyOf(obj)) ||
    obj?.school?.school_name ||
    obj?.School ||
    obj?.school_name ||
    (typeof obj?.school === "string" ? obj.school : "") ||
    "Unassigned";

// Turn an admin-picked hex into the {bg, bd, tx} triple the blocks/capsules use:
// a light translucent fill + a solid border/text of the chosen colour.
export const triuneFromHex = (hex) => {
    const h = (hex || "").trim();
    if (!/^#([0-9a-f]{6})$/i.test(h)) return null;
    return { bg: `${h}22`, bd: h, tx: h };
};
// The colour the calendar draws a school in: the admin's saved choice, else the
// deterministic palette fallback. Single source of truth — the calendar blocks
// and the "Manage Colors" legend both go through it, so the legend can never
// drift from what is actually drawn.
export const schoolColorFor = (key, schoolColors) =>
    triuneFromHex(schoolColors?.[canonicalSchoolId(key)]) || schoolColorForKey(key);
// Event bookings get a fixed dark yellow whatever school they belong to, so an
// admin can pick them out of the grid at a glance.
export const EVENT_COLOR_HEX = "#B8860B";

// A booking's colour, in priority order:
//   1. an explicitly assigned booking type (Course, Exam, …) — a deliberate
//      per-booking admin action, so it still wins;
//   2. the event flag — ranked above school, so an event never hides inside its
//      school's colour;
//   3. the booking's school, which is the default coding.
const resolveColorObj = (colorTypeId, colorTypeMap, fallbackObj, schoolColors, isEvent = false) => {
    const hex = colorTypeId && colorTypeMap ? colorTypeMap[colorTypeId] : null;
    const assigned = triuneFromHex(hex);
    if (assigned) return assigned;
    if (isEvent) return triuneFromHex(EVENT_COLOR_HEX);
    return schoolColorFor(schoolKeyOf(fallbackObj), schoolColors);
};

const runsForRow = (row, slotSource) => {
    const runs = slotRunsFor({
        room_booking_request_slots: slotSource || [],
        start_time: row.start_time,
        end_time: row.end_time,
    });
    if (!runs.length) return [[moment.utc(row.start_time), moment.utc(row.end_time)]];
    const outerStart = runs[0][0];
    const outerEnd = runs[runs.length - 1][1];
    if (!outerStart.isSame(moment.utc(row.start_time)) || !outerEnd.isSame(moment.utc(row.end_time))) {
        return [[moment.utc(row.start_time), moment.utc(row.end_time)]];
    }
    return runs;
};
// Times are stored/returned as UTC; render them in UTC like the student page —
// callers pass a moment already parsed with moment.utc(...).
const fracOf = (m) => m.hour() + m.minute() / 60;
// "12:00 PM - 12:30 PM, 1:30 PM - 2:30 PM" — so the details popup reads
// correctly regardless of which chip of a split booking was opened.
const runsLabelFor = (runs) =>
    runs.map(([s, e]) => `${s.format("hh:mm A")} - ${e.format("hh:mm A")}`).join(", ");
const runsMinutes = (runs) => runs.reduce((sum, [s, e]) => sum + e.diff(s, "minutes"), 0);

// Group rooms by tower and flatten their booked slots into chart bookings.
//   • room_bookings (status "Booked")             → solid, approved blocks
//   • room_booking_requests (status "Under Review") → dashed, pending blocks
const buildChartData = (rooms, colorTypeMap, schoolColors) => {
    const byTower = new Map();
    (rooms || []).forEach((r) => {
        const key = r.tower_id ?? r.tower ?? "Other";
        if (!byTower.has(key)) byTower.set(key, { name: r.tower || "Other", rooms: [] });
        byTower.get(key).rooms.push({
            id: r.id,
            name: r.room_name,
            capacity: r.capacity,
            status: r.room_bookings && r.room_bookings.length ? "Occupied" : "Open",
        });
    });
    const groups = Array.from(byTower.values()).map((t, i) => ({
        id: `tower-${i}`,
        name: t.name,
        rooms: t.rooms,
    }));

    const bookings = [];
    (rooms || []).forEach((r) => {
        (r.room_bookings || []).forEach((b) => {
            const user = b.CoworkUser || b.User || b.coworkuser || {};
            // A confirmed booking carries its type via the originating request.
            // Sequelize names the belongsTo include by the singular model name,
            // but accept the plural too in case the association is aliased.
            const nestedReq = b.room_booking_request || b.room_booking_requests;
            const colorTypeId = nestedReq?.color_type_id || null;
            // Event flag can sit on the confirmed booking or its originating
            // request; surfaced in the hover tooltip.
            const isEvent = !!(b.isEvent ?? nestedReq?.isEvent);
            const runs = runsForRow(b, nestedReq?.room_booking_request_slots);
            const isSplit = runs.length > 1;
            const runsLabel = isSplit ? runsLabelFor(runs) : null;
            const runsTotalHours = isSplit ? runsMinutes(runs) / 60 : null;
            runs.forEach(([start, end], idx) => {
                bookings.push({
                    id: b.id,
                    requestId: b.request_id,
                    roomId: r.id,
                    date: start.format("YYYY-MM-DD"),
                    start: fracOf(start),
                    end: fracOf(end),
                    title: b.meeting_title || "Booking",
                    bookedBy: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
                    color: colorFor(b.id),
                    colorObj: resolveColorObj(colorTypeId, colorTypeMap, b, schoolColors, isEvent),
                    colorTypeId,
                    isEvent,
                    schoolKey: schoolKeyOf(b),
                    schoolName: schoolLabelOf(b),
                    // Carried so the edit form can prefill without a second fetch.
                    attendees: b.attendees || nestedReq?.attendees || [],
                    needInstructor: !!(b.needInstructor ?? nestedReq?.needInstructor),
                    // Who raised this booking — used to let a non-admin see their own
                    // request while other people's stay hidden.
                    ownerId: b.user_id ?? nestedReq?.user_id ?? user.id ?? null,
                    // A confirmed booking carries its ticket number via the
                    // originating request; surfaced in the hover tooltip.
                    ticketId: nestedReq?.ticket_id ?? null,
                    status: "Approved",
                    pending: false,
                    // A booking with a gap between its slots renders as several
                    // chips sharing this same id/requestId — see runsForRow.
                    // Moving/resizing/editing details is disabled for these
                    // (canMoveBooking/canEditDetails): re-timing one chip can't
                    // unambiguously re-time "the booking".
                    isSplit,
                    runsLabel,
                    runsTotalHours,
                    runKey: isSplit ? `${b.id}-${idx}` : String(b.id),
                });
            });
        });
        (r.room_booking_requests || []).forEach((req) => {
            if (!isPendingStatus(req.status)) return;
            const user = req.requestingUser || req.User || req.CoworkUser || {};
            const runs = runsForRow(req, req.room_booking_request_slots);
            const isSplit = runs.length > 1;
            const runsLabel = isSplit ? runsLabelFor(runs) : null;
            const runsTotalHours = isSplit ? runsMinutes(runs) / 60 : null;
            runs.forEach(([start, end], idx) => bookings.push({
                id: `req-${req.id}`,
                requestId: req.id,
                roomId: r.id,
                date: start.format("YYYY-MM-DD"),
                start: fracOf(start),
                end: fracOf(end),
                title: req.meeting_title || "Request",
                bookedBy: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
                color: colorFor(req.id),
                colorObj: resolveColorObj(req.color_type_id || null, colorTypeMap, req, schoolColors, !!req.isEvent),
                colorTypeId: req.color_type_id || null,
                isEvent: !!req.isEvent,
                schoolKey: schoolKeyOf(req),
                schoolName: schoolLabelOf(req),
                attendees: req.attendees || [],
                needInstructor: !!req.needInstructor,
                ownerId: req.user_id ?? req.requested_by ?? user.id ?? null,
                ticketId: req.ticket_id ?? null,
                status: req.status,
                pending: true,
                isSplit,
                runsLabel,
                runsTotalHours,
                runKey: isSplit ? `req-${req.id}-${idx}` : `req-${req.id}`,
            }));
        });
    });
    return { groups, bookings };
};

const BookingPills = ({ booking, compact }) => {
    if (compact) {
        return (
            <WrapperBox
                borderRadius={6}
                padding={"4px 6px"}
                background={globalTheme.colors.white1}
                Width={"100%"}
                overflow={"hidden"}
            >
                <Flexbox gap={2} style={{ minWidth: 0 }}>
                    <Text
                        font10
                        inter
                        medium
                        black3
                        style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                            width: "100%",
                        }}
                    >
                        For: {booking.title}
                    </Text>
                    <Flexbox gap={3} alignCenter noWrap>
                        <Clock />
                        <Text font10 inter semiBold black3>
                            {formatTime(booking.start)}
                        </Text>
                    </Flexbox>
                </Flexbox>
            </WrapperBox>
        );
    }

    return (
        <>
            <WrapperBox
                borderRadius={20}
                padding={"3px 8px"}
                background={globalTheme.colors.white1}
                maxWidth={"max-content"}
                overflow={"hidden"}
            >
                <Flexbox gap={6} noWrap alignCenter style={{ minWidth: 0 }}>
                    <Text
                        font10
                        inter
                        medium
                        black3
                        style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                            flex: "0 1 auto",
                        }}
                    >
                        For: <strong>{booking.title}</strong>
                    </Text>
                    <Flexbox gap={3} alignCenter maxWidth="max-content" style={{ flexShrink: 0 }}>
                        <Clock />
                        <Text font10 inter semiBold black3 style={{ whiteSpace: "nowrap" }}>
                            {formatTime(booking.start)} - {formatTime(booking.end)}
                        </Text>
                    </Flexbox>
                </Flexbox>
            </WrapperBox>
            {booking.bookedBy && (
                <WrapperBox
                    borderRadius={20}
                    padding={"3px 8px"}
                    background={globalTheme.colors.white1}
                    maxWidth={"max-content"}
                    overflow={"hidden"}
                >
                    <Text
                        font10
                        inter
                        medium
                        black3
                        style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                        }}
                    >
                        Booked by: <strong>{booking.bookedBy}</strong>
                    </Text>
                </WrapperBox>
            )}
        </>
    );
};

const ResizeHandle = ({ side, onMouseDown }) => (
    <div
        onMouseDown={onMouseDown}
        onClick={(e) => e.stopPropagation()}
        style={{
            position: "absolute",
            top: -1,
            bottom: -1,
            [side]: -8,
            width: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ew-resize",
            zIndex: 7,
        }}
    >
        <span
            style={{
                width: 16,
                height: 24,
                borderRadius: 6,
                background: globalTheme.colors.black4,
                color: globalTheme.colors.white1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                lineHeight: 1,
                boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                userSelect: "none",
            }}
        >
            ⟷
        </span>
    </div>
);

// Pastel palette for the Week-view capsules — light fill + matching accent,
// keyed off the booking's PALETTE colour so each block keeps a stable hue.
const CAPSULE_COLORS = {
    green: { bg: "#E7F6EC", bd: "#41B871", tx: "#1E7E45" },
    yellow: { bg: "#FEF4E6", bd: "#E8A23D", tx: "#B9791B" },
    red: { bg: "#FDECEC", bd: "#F0685F", tx: "#C0392B" },
    blue: { bg: "#EAF1FF", bd: "#5B8DEF", tx: "#2F62C4" },
    purple: { bg: "#F1ECFB", bd: "#9B6EE0", tx: "#6B3FBF" },
};
const CAPSULE_H = 26; // px per capsule (used to size the week row)

// Compact booking capsule for the Week view: one per booking, stacked
// vertically inside a room×day cell. pointerEvents stay on the capsule so the
// empty area of the cell still starts a slide-to-create selection.
const BookingCapsule = ({ booking, selected, conflict, editable = true, own = false, onClick, onDoubleClick, onMouseDown, onMouseEnter, onMouseLeave }) => {
    const pending = booking.pending;
    // Under-review requests render as an anonymous dashed pill with NO details —
    // several requests can compete for the same slot, so we don't reveal any one
    // of them. Your OWN pending request is the exception: you raised it, so it
    // shows its times and can be edited. An approved booking that overlaps an
    // under-review request keeps its details but gains a dashed border to flag
    // the pending competition. Approved blocks are coloured by school.
    const blank = pending && !own;
    const dashed = pending || conflict;
    const c = pending
        ? { bg: "rgba(232, 162, 61, 0.10)", bd: "rgba(232, 162, 61, 0.75)", tx: "#B9791B" }
        : (booking.colorObj || CAPSULE_COLORS[booking.color] || CAPSULE_COLORS.blue);
    return (
        <div
            className="bookingBlock"
            data-bookingid={booking.id}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onMouseDown={onMouseDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                pointerEvents: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                alignSelf: "flex-start",
                maxWidth: "100%",
                boxSizing: "border-box",
                padding: "3px 10px",
                borderRadius: 20,
                background: c.bg,
                border: dashed ? `1.5px dashed ${c.bd}` : `1px solid ${c.bd}`,
                outline: selected ? "2px solid rgba(80,120,255,0.9)" : "none",
                outlineOffset: 1,
                cursor: editable ? "grab" : "pointer",
                flexShrink: 0,
                overflow: "hidden",
                minWidth: blank ? 54 : undefined,
                justifyContent: blank ? "center" : undefined,
            }}
        >
            <span style={{ display: "inline-flex", flexShrink: 0, opacity: blank ? 0.6 : 1 }}>
                <Clock />
            </span>
            {/* Under-review slots are anonymous: a dashed pill with no time/label. */}
            {!blank && (
                <span
                    style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: c.tx,
                        fontFamily: "inherit",
                        whiteSpace: "nowrap",
                    }}
                >
                    {formatTime(booking.start)} – {formatTime(booking.end)}
                </span>
            )}
        </div>
    );
};

const cellKeyFor = (roomId, isWeek, colMeta) =>
    isWeek ? `${roomId}|d${colMeta}` : `${roomId}|h${colMeta}`;

// Frozen left-pane cell: room name + capacity. rowHeight must match the
// right-pane row so the two panes stay aligned (Week rows grow with content).
const RoomNameCell = ({ room, rowHeight = ROW_H }) => (
    <RoomCol
        column
        gap={6}
        justiCenter
        padding={"0 16px"}
        style={{ height: rowHeight, boxSizing: "border-box", overflow: "hidden" }}
    >
        {/* The column is narrow and fixed, so long room names ellipsis away —
            the title makes the full name readable on hover. */}
        <Text
            font12
            inter
            semiBold
            black3
            title={room.name}
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
            {room.name}
        </Text>
        <Flexbox gap={6} alignCenter noWrap>
            <Users width={"14px"} height={"14px"} color={globalTheme.colors.nuetral6} />
            <Text font10 inter medium nuetral6>
                {room.capacity}
            </Text>
            <Text font10 inter medium nuetral6>
                ·
            </Text>
            <Text font10 inter medium nuetral6>
                {room.status}
            </Text>
        </Flexbox>
    </RoomCol>
);

const SKELETON_BAR = {
    display: "block",
    height: 12,
    borderRadius: 6,
    background: "linear-gradient(90deg, #f0f0f0 25%, #e3e3e3 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    // Keyframes live in GlobalStyles.
    animation: "shimmer 1.5s infinite",
};

// Placeholder rows for rooms still loading. Both panes render the same count at
// the fixed ROW_H so the frozen left column stays aligned with the grid.
const RoomNameSkeletonCell = () => (
    <RoomCol
        column
        gap={8}
        justiCenter
        padding={"0 16px"}
        style={{ height: ROW_H, boxSizing: "border-box", overflow: "hidden" }}
    >
        <span style={{ ...SKELETON_BAR, width: 120 }} />
        <span style={{ ...SKELETON_BAR, width: 70, height: 10 }} />
    </RoomCol>
);

// Right-pane counterpart: a faint block roughly where a booking would sit.
const RoomCellsSkeletonRow = ({ width }) => (
    <div
        style={{
            height: ROW_H,
            boxSizing: "border-box",
            borderBottom: "1px solid #eee",
            width: width || "100%",
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
        }}
    >
        <span style={{ ...SKELETON_BAR, width: "38%", height: 34, borderRadius: 8 }} />
    </div>
);

// Right-pane row: the time/date cells, slide-select overlay and booking blocks.
const RoomCellsRow = ({
    room,
    bookings,
    isWeek,
    weekDays,
    cellWidth,
    colPx,
    onBlockClick,
    onBlockDblClick,
    onBlockHover,
    onBlockLeave,
    activeResizeId,
    onBeginResize,
    selectedCellKeys,
    onCellClick,
    clipboardId,
    selectedIds,
    onBlockMouseDown,
    onRowMouseDown,
    dragSelect,
    rowHeight = ROW_H,
    // (booking) => boolean — admins get every block, a class user only their own.
    canEditBooking = () => true,
    // (booking) => boolean — narrower than the above: may this block be re-timed
    // (dragged / cut / resized)? False for a class user's approved bookings,
    // which they may only copy. Drives the grab cursor.
    canMoveBooking = () => true,
    // (booking) => boolean — true when the viewer raised the block themselves.
    isOwnBooking = () => false,
    // (colMeta) => boolean — the cell's slot has already finished.
    isPastColumn = () => false,
    // (colMeta) => boolean — the cell's day is past the viewer's booking window.
    isBeyondColumn = () => false,
    beyondWindowMessage = "This date is outside the booking window",
}) => {
    const columns = isWeek ? weekDays : HOURS;
    const colsWidth = columns.length * colPx;
    // Week columns flex to fill the pane (no horizontal scroll), so bookings are
    // positioned in % of the row; Day/hour columns are fixed px and scroll.
    const weekPct = (n) => `calc(${n} * (100% / ${weekDays.length}))`;

    const getLeft = (booking) => {
        if (isWeek) {
            const idx = Math.max(0, weekDays.findIndex((d) => toIsoDate(d) === booking.date));
            return `calc(${weekPct(idx)} + 2px)`;
        }
        return `${(booking.start - START_HOUR) * cellWidth + 2}px`;
    };
    const getWidth = (booking) => {
        if (isWeek) return `calc(${weekPct(1)} - 4px)`;
        return `${(booking.end - booking.start) * cellWidth - 4}px`;
    };

    // Live slide-selection rectangle.
    let overlay = null;
    if (dragSelect && dragSelect.roomId === room.id) {
        if (dragSelect.week && isWeek) {
            const a = Math.min(dragSelect.a, dragSelect.b);
            const b = Math.max(dragSelect.a, dragSelect.b);
            overlay = { left: `calc(${weekPct(a)} + 2px)`, width: `calc(${weekPct(b - a + 1)} - 4px)`, label: `${b - a + 1} day${b - a ? "s" : ""}` };
        } else if (!dragSelect.week && !isWeek) {
            const a = Math.min(dragSelect.a, dragSelect.b);
            const b = Math.max(dragSelect.a, dragSelect.b);
            overlay = { left: `${(a - START_HOUR) * cellWidth + 2}px`, width: `${Math.max(0, (b - a) * cellWidth - 4)}px`, label: `${formatTime(a)} - ${formatTime(b)}` };
        }
    }

    return (
        <div
            style={{ position: "relative", width: isWeek ? "100%" : colsWidth, height: rowHeight, userSelect: "none" }}
            data-roomrow="1"
            data-roomid={room.id}
            onMouseDown={(e) => onRowMouseDown?.(e, room, e.currentTarget)}
        >
            <div style={{ display: "flex", height: "100%" }}>
                {columns.map((col, i) => {
                    const colMeta = isWeek ? toIsoDate(col) : col;
                    const key = cellKeyFor(room.id, isWeek, colMeta);
                    const isSelected = !!selectedCellKeys && selectedCellKeys.includes(key);
                    // A slot that has already finished can't be booked or pasted
                    // into, so it reads as disabled. Bookings drawn on top of it
                    // stay fully interactive — you can still copy from the past.
                    const past = isPastColumn(colMeta);
                    // ...and the same at the other end: a day past the viewer's
                    // booking window is inert too, for the same reasons.
                    const beyond = !past && isBeyondColumn(colMeta);
                    const blocked = past || beyond;
                    return (
                        <TimeCell
                            key={i}
                            onClick={(e) => !blocked && onCellClick?.(e, room.id, colMeta)}
                            title={
                                past
                                    ? "This time has already passed"
                                    : beyond
                                        ? beyondWindowMessage
                                        : undefined
                            }
                            style={{
                                width: isWeek ? undefined : colPx,
                                flex: isWeek ? "1 1 0" : `0 0 ${colPx}px`,
                                minWidth: isWeek ? 0 : undefined,
                                height: "100%",
                                cursor: blocked ? "not-allowed" : "pointer",
                                background: isSelected
                                    ? "rgba(80,120,255,0.14)"
                                    : blocked
                                        ? PAST_CELL_BG
                                        : undefined,
                                backgroundImage: !isWeek && !isSelected && !blocked ? QUARTER_LINES : undefined,
                                boxShadow: isSelected ? "inset 0 0 0 2px rgba(80,120,255,0.55)" : undefined,
                            }}
                        />
                    );
                })}
            </div>

            {overlay && (
                <div
                    style={{
                        position: "absolute",
                        top: 8,
                        height: rowHeight - 16,
                        left: overlay.left,
                        width: overlay.width,
                        background: "rgba(80,120,255,0.18)",
                        border: "2px solid rgba(80,120,255,0.75)",
                        borderRadius: 8,
                        zIndex: 5,
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Text font10 inter semiBold style={{ color: "rgba(40,70,200,1)" }}>
                        {overlay.label}
                    </Text>
                </div>
            )}

            {/* Week view: one capsule column per day, listing ALL of the room's
                bookings for that day (Day view keeps positioned blocks below).
                The column is content-sized with a max-height cap so it only
                covers its capsules — empty space below stays free for
                slide-to-create — and scrolls once it hits the cap. */}
            {isWeek &&
                weekDays.map((day, di) => {
                    const iso = toIsoDate(day);
                    const dayBookings = bookings.filter((b) => b.date === iso);
                    if (!dayBookings.length) return null;
                    // Pending (under-review) blocks for this room+day — used to
                    // flag approved bookings that overlap a pending request so
                    // they get a dashed border.
                    const pendingForDay = dayBookings.filter((b) => b.pending);
                    return (
                        <div
                            key={iso}
                            style={{
                                position: "absolute",
                                top: 5,
                                maxHeight: rowHeight - 10,
                                left: `calc(${weekPct(di)} + 3px)`,
                                width: `calc(${weekPct(1)} - 6px)`,
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                                overflowY: "auto",
                                zIndex: 2,
                            }}
                        >
                            {dayBookings.map((booking) => (
                                <BookingCapsule
                                    key={booking.runKey || booking.id}
                                    booking={booking}
                                    editable={canEditBooking(booking)}
                                    own={isOwnBooking(booking)}
                                    selected={!!selectedIds && selectedIds.includes(booking.id)}
                                    conflict={
                                        !booking.pending &&
                                        pendingForDay.some(
                                            (p) => booking.start < p.end && booking.end > p.start
                                        )
                                    }
                                    onClick={(e) => onBlockClick?.(e, booking, room)}
                                    onDoubleClick={() => onBlockDblClick?.(booking, room)}
                                    onMouseEnter={(e) => onBlockHover?.(e, booking, room)}
                                    onMouseLeave={() => onBlockLeave?.()}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        onBlockMouseDown?.(e, booking, room);
                                    }}
                                />
                            ))}
                        </div>
                    );
                })}

            {!isWeek && bookings.map((booking) => {
                const left = getLeft(booking);
                const width = getWidth(booking);
                const compact = booking.short || isWeek;
                const isResizing = activeResizeId === booking.id && !isWeek;
                const isSelected = !!selectedIds && selectedIds.includes(booking.id);
                const isClipped = clipboardId === booking.id;
                const pending = booking.pending;
                // Approved block overlapping an under-review request → dashed border.
                const pendingOverlap = !pending && bookings.some(
                    (o) => o.pending && booking.start < o.end && booking.end > o.start
                );

                return (
                    <BookingBlock
                        key={booking.runKey || booking.id}
                        className="bookingBlock"
                        data-bookingid={booking.id}
                        color={booking.color}
                        spaceBW
                        alignCenter
                        padding={compact ? "4px" : "6px 10px"}
                        gap={6}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            onBlockMouseDown?.(e, booking, room);
                        }}
                        onClick={(e) => onBlockClick?.(e, booking, room)}
                        onDoubleClick={() => onBlockDblClick?.(booking, room)}
                        onMouseEnter={(e) => onBlockHover?.(e, booking, room)}
                        onMouseLeave={() => onBlockLeave?.()}
                        style={{
                            left,
                            width,
                            overflow: isResizing ? "visible" : "hidden",
                            opacity: pending ? 0.95 : 1,
                            outline: isResizing
                                ? `2px solid ${globalTheme.colors.black4}`
                                : isSelected
                                    ? `2px solid rgba(80,120,255,0.9)`
                                    : "none",
                            outlineOffset: 1,
                            background: pending
                                ? "rgba(232,162,61,0.10)"
                                : (booking.colorObj?.bg || undefined),
                            border: pending
                                ? `2px dashed ${DASH_PENDING}`
                                : pendingOverlap
                                    ? `2px dashed ${booking.colorObj?.bd || DASH_PENDING}`
                                    : isClipped
                                        ? "2px dashed rgba(80,120,255,0.9)"
                                        : undefined,
                            zIndex: isResizing || isSelected ? 6 : 1,
                            cursor: isResizing ? "default" : canMoveBooking(booking) ? "grab" : "pointer",
                        }}
                    >
                        {/* Other people's under-review slots show no details — just a
                            blank dashed block. Your own pending request shows its
                            times, so you can find and edit it. */}
                        {(!pending || isOwnBooking(booking)) && <BookingPills booking={booking} compact={compact} />}
                        {isResizing && (
                            <>
                                <ResizeHandle side="left" onMouseDown={(e) => onBeginResize(e, booking, "left")} />
                                <ResizeHandle side="right" onMouseDown={(e) => onBeginResize(e, booking, "right")} />
                            </>
                        )}
                    </BookingBlock>
                );
            })}
        </div>
    );
};

const POPUP_FADE_MS = 400;

const BookingCalendar = ({
    selectedDate,
    view = "Day",
    rooms = [],
    loading = false,
    // Infinite scroll over the room list: the grid pulls the next page of rooms
    // (and with them their bookings) as the admin scrolls towards the bottom.
    hasMoreRooms = false,
    loadingMoreRooms = false,
    onLoadMoreRooms,
    filterStatus = "",
    onSave,
    onViewRequests,
    onCreateBooking,
    // True while a slide-to-create request is in flight; drives the create
    // popup's loading state so the button disables and reads "Creating…".
    creatingBooking = false,
    // Edit the details of an under-review request. Omitted (undefined) → the
    // "Edit Details" affordances never render.
    onEditBooking,
    // True while an edit is in flight; drives the edit popup's loading state.
    savingBookingEdit = false,
    onCancelBooking,
    colorTypes = [],
    // schoolId → hex, the admin's per-school colour choices. Drives the block
    // colour for every booking whose school appears here.
    schoolColors = {},
    onAssignColorType,
    // Pending ("Under Review") slots are an admin review queue. Admins see every
    // one of them; everyone else only ever sees the requests they raised
    // themselves, matched on `currentUserId`.
    canViewPending = true,
    currentUserId = null,
    isisClassAdmin = false,
    // How far ahead this viewer may book (Date, inclusive). Days past it are inert
    // on the grid — no slide-to-create, no paste target — exactly like past days.
    // Null leaves the board unbounded.
    maxBookingDate = null,
    maxBookingDateMessage = "This date is outside the booking window",
}) => {
    const [collapsed, setCollapsed] = useState({});
    const [activeBooking, setActiveBooking] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupStep, setPopupStep] = useState("details");
    const [confirmMode, setConfirmMode] = useState("cancel");
    const [cancelReason, setCancelReason] = useState("");
    const clearTimerRef = useRef(null);

    const [expandedDay, setExpandedDay] = useState(null);

    const [selectedIds, setSelectedIds] = useState([]); // multi-select (Ctrl/Shift-click)
    const anchorRef = useRef(null); // range anchor for Shift-click
    const [selectedCells, setSelectedCells] = useState([]); // paste-target cells [{ roomId, meta }]
    const cellAnchorRef = useRef(null); // range anchor for Shift-click on cells
    const [clipboard, setClipboard] = useState(null);
    // Whole-week clipboard: snapshot of a week's bookings (by weekday offset) so
    // the admin can stamp a recurring week onto another week.
    const [weekClipboard, setWeekClipboard] = useState(null); // { count, items: [{ sourceId, dow, roomId, start, end, title, color }] }
    const clipboardRef = useRef(null);
    const weekClipboardRef = useRef(null);
    const moveDragRef = useRef(null);
    const justMovedRef = useRef(false);
    const [ghost, setGhost] = useState(null);
    const tmpIdRef = useRef(0);

    const slideRef = useRef(null);
    const [dragSelect, setDragSelect] = useState(null);
    const [createSel, setCreateSel] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [recurBooking, setRecurBooking] = useState(null);
    const [recurOpen, setRecurOpen] = useState(false);
    // Edit an under-review request's details — same form as create, prefilled.
    const [editSel, setEditSel] = useState(null);
    const [editOpen, setEditOpen] = useState(false);

    // id -> hex, so buildChartData can colour a block by its assigned type.
    const colorTypeMap = React.useMemo(() => {
        const map = {};
        (colorTypes || []).forEach((t) => { map[t.id] = t.color; });
        return map;
    }, [colorTypes]);

    // Re-derive when the admin repaints a school, so blocks recolour without a
    // refetch. Keyed on the serialised map — the parent rebuilds the object on
    // every render, so an identity dep would loop.
    const schoolColorsKey = JSON.stringify(schoolColors || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const schoolColorsMemo = React.useMemo(() => schoolColors || {}, [schoolColorsKey]);

    const { groups, bookings: derivedBookings } = React.useMemo(
        () => buildChartData(rooms, colorTypeMap, schoolColorsMemo),
        [rooms, colorTypeMap, schoolColorsMemo]
    );

    // ---- Infinite scroll over the room list ----
    // Nothing on screen yet vs. appending the next page — the first fills the
    // grid with placeholder rows, the second appends a few below the last room.
    const showInitialSkeleton = loading && rooms.length === 0;
    const showMoreSkeleton = loadingMoreRooms && rooms.length > 0;
    const gridScrollRef = useRef(null);

    const requestMoreRooms = React.useCallback(() => {
        if (!onLoadMoreRooms || !hasMoreRooms || loading || loadingMoreRooms) return;
        onLoadMoreRooms();
    }, [onLoadMoreRooms, hasMoreRooms, loading, loadingMoreRooms]);

    const handleGridScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight > ROOM_SCROLL_THRESHOLD) return;
        requestMoreRooms();
    };

    // A first page shorter than the viewport leaves nothing to scroll — top it
    // up so more rooms can still be reached. Collapsing a group also shrinks the
    // grid below the viewport (without changing rooms.length), which kills scroll
    // events, so re-run on `collapsed` too or paging would stall until re-expand.
    useEffect(() => {
        const el = gridScrollRef.current;
        if (!el) return;
        if (el.scrollHeight <= el.clientHeight + ROOM_SCROLL_THRESHOLD) requestMoreRooms();
    }, [rooms.length, requestMoreRooms, collapsed]);

    const [bookings, setBookings] = useState([]);
    const [baseline, setBaseline] = useState([]);
    const [activeResizeId, setActiveResizeId] = useState(null);
    const [tip, setTip] = useState(null);
    const dragRef = useRef(null);
    const clickTimerRef = useRef(null);
    const justResizedRef = useRef(false);
    const tipHideTimerRef = useRef(null);

    // ---- Undo / redo for UNSAVED edits ----
    // `bookings` is the single editable model, so history = snapshots of that
    // array. Each discrete user action calls pushHistory() once *before* it
    // mutates; drag gestures (resize / move) snapshot at gesture start, not per
    // mousemove. History is wiped whenever a refetch/save installs a new baseline
    // (see the sync effect below), so saved changes are no longer undoable.
    const [past, setPast] = useState([]);
    const [future, setFuture] = useState([]);
    const bookingsRef = useRef([]);
    useEffect(() => {
        bookingsRef.current = bookings;
    }, [bookings]);

    const pushHistory = () => {
        setPast((p) => [...p, bookingsRef.current]);
        setFuture([]);
    };
    const undo = () => {
        if (!past.length) return;
        const prev = past[past.length - 1];
        setPast(past.slice(0, -1));
        setFuture([bookingsRef.current, ...future]);
        setBookings(prev);
        // Drop transient selection/clipboard so nothing points at a stale block.
        setSelectedIds([]);
        setSelectedCells([]);
        setClipboard(null);
        setActiveResizeId(null);
    };
    const redo = () => {
        if (!future.length) return;
        const next = future[0];
        setFuture(future.slice(1));
        setPast([...past, bookingsRef.current]);
        setBookings(next);
        setSelectedIds([]);
        setSelectedCells([]);
        setClipboard(null);
        setActiveResizeId(null);
    };
    const canUndo = past.length > 0;
    const canRedo = future.length > 0;

    useEffect(() => {
        // A CUT is a removal that hasn't been saved yet, so a refetch brings its
        // blocks straight back — while the clipboard still holds them. Re-apply
        // the cut to the fresh data, otherwise the originals reappear and the
        // paste either duplicates the booking or, pasted back onto the slot the
        // original just reclaimed, is rejected as a conflict and silently
        // changes nothing (Save stays disabled). This matters because the cut
        // workflow the banner advertises — "go to the target week and paste" —
        // ALWAYS crosses a refetch.
        const cutIds = new Set(
            [
                clipboardRef.current?.mode === "cut" ? clipboardRef.current.booking?.id : null,
                ...(weekClipboardRef.current?.mode === "cut"
                    ? (weekClipboardRef.current.items || []).map((it) => it.booking?.id)
                    : []),
            ].filter(Boolean)
        );
        setBookings(cutIds.size ? derivedBookings.filter((b) => !cutIds.has(b.id)) : derivedBookings);
        // Baseline stays the SERVER truth even with a cut applied — that is what
        // makes the pasted block read as "moved" rather than unchanged.
        setBaseline(derivedBookings);
        setPast([]);
        setFuture([]);
        setActiveResizeId(null);
        setSelectedIds([]);
        anchorRef.current = null;
        setSelectedCells([]);
        cellAnchorRef.current = null;
        // Both clipboards survive the refetch: copying (or cutting) a booking and
        // then navigating to the target day/week is the whole point, and every
        // navigation refetches. A cut stays consistent because its blocks are
        // re-removed above; Esc still puts them back.
    }, [derivedBookings]);

    // Read inside the refetch sync above, which must NOT re-run when a clipboard
    // changes — it would reset every unsaved edit on the board.
    useEffect(() => { clipboardRef.current = clipboard; }, [clipboard]);
    useEffect(() => { weekClipboardRef.current = weekClipboard; }, [weekClipboard]);

    useEffect(() => {
        if (view !== "Week") setExpandedDay(null);
    }, [view]);

    const groupOf = (roomId) =>
        groups.find((g) => g.rooms.some((r) => r.id === roomId)) || null;
    const roomNameFor = (roomId) => {
        for (const g of groups) {
            const r = g.rooms.find((rm) => rm.id === roomId);
            if (r) return r.name;
        }
        return "";
    };

    const date = selectedDate instanceof Date ? selectedDate : new Date(2026, 2, 3);

    const weekDays = React.useMemo(() => {
        const start = startOfWeek(date);
        return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }, [date]);

    // Navigating to a different week returns to the full week view: an expanded
    // single day belongs to the week it was opened in, so keeping it open after
    // moving to another week would strand the user on a day that isn't visible.
    const weekStartIso = React.useMemo(() => toIsoDate(weekDays[0]), [weekDays]);
    useEffect(() => {
        setExpandedDay(null);
    }, [weekStartIso]);

    const showWeekStrip = view === "Week";
    const isWeek = view === "Week" && !expandedDay;
    const activeDate = expandedDay ? fromIsoDate(expandedDay) : date;
    const dayIso = toIsoDate(activeDate);

    const colPx = isWeek ? WEEK_COL_PX : HOUR_COL_PX;
    const cellWidth = colPx;
    const bodyColumns = isWeek ? weekDays : HOURS;
    const bodyColsWidth = bodyColumns.length * colPx;

    const pendingChanges = React.useMemo(() => {
        const updates = [];
        const creates = [];
        bookings.forEach((b) => {
            if (b._new) {
                creates.push({
                    sourceBookingId: b._sourceId || null,
                    // Set instead when the copy came from a pending request,
                    // which has no confirmed booking row to duplicate from.
                    sourceRequestId: b._sourceRequestId || null,
                    room_id: b.roomId,
                    date: b.date,
                    start: b.start,
                    end: b.end,
                });
                return;
            }
            // Split-booking chips share one id and can't be moved/resized, so
            // they never carry an edit. Diffing them by id would match each chip
            // against the FIRST chip's baseline and stage a bogus update.
            if (b.isSplit) return;
            const base = baseline.find((o) => o.id === b.id);
            if (base && (base.start !== b.start || base.end !== b.end || base.roomId !== b.roomId || base.date !== b.date)) {
                // A pending block has no confirmed booking row — it IS the
                // request, so it is saved by requestId. `id` there is the
                // synthetic "req-<uuid>" key and must not be sent as a bookingId.
                updates.push({
                    id: b.id,
                    isRequest: !!b.pending,
                    requestId: b.requestId,
                    room_id: b.roomId,
                    date: b.date,
                    start: b.start,
                    end: b.end,
                });
            }
        });
        return { updates, creates };
    }, [bookings, baseline]);
    const pendingCount = pendingChanges.updates.length + pendingChanges.creates.length;
    const dirty = pendingCount > 0;

    // Warn on reload / tab-close while edits are unsaved (in-app nav is guarded in
    // the dashboard). The prompt text is browser-controlled.
    useEffect(() => {
        if (!dirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    // Single-selection id (drives resize / Make-recurring / single copy).
    const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
    const selectedBooking = bookings.find((b) => b.id === selectedId) || null;

    // Who raised this block. Drives both visibility (below) and edit rights.
    const isOwnBooking = React.useCallback(
        (b) =>
            currentUserId != null && b?.ownerId != null && String(b.ownerId) === String(currentUserId),
        [currentUserId]
    );
    // An admin may edit the whole schedule; a class user may only edit blocks
    // they raised themselves — their approved bookings (re-timing one sends it
    // back to "Under Review") and their own still-pending requests.
    const canEditBooking = React.useCallback(
        (b) => !!b && (isisClassAdmin || isOwnBooking(b)),
        [isisClassAdmin, isOwnBooking]
    );
    // MOVING a block — drag, cut-paste, resize — rewrites a booking that already
    // exists, so a class user may only do it while their request is still under
    // review. Their approved bookings are decided: they can be COPIED (which
    // raises a fresh request) but not moved, because moving one used to cancel
    // the confirmed booking and drop it back into the review queue. An admin
    // owns the schedule and may move anything. An unsaved local copy (`_new`)
    // stays adjustable until it is saved — there is no server row to disturb.
    // A split booking (gap between its slots, rendered as several chips that
    // share one id — see runsForRow in buildChartData) can't be moved/resized:
    // re-timing one chip can't unambiguously re-time "the booking", and doing
    // so would send a bogus id back to the save/duplicate endpoints.
    const canMoveBooking = React.useCallback(
        (b) => !!b && !b.isSplit && (isisClassAdmin || (isOwnBooking(b) && (!!b.pending || !!b._new))),
        [isisClassAdmin, isOwnBooking]
    );
    // Shown when a class user tries to move a booking that is already approved,
    // or anyone tries to move/resize a split one.
    const warnApprovedMove = (b) =>
        toast.error(
            b?.isSplit
                ? "This booking has a gap between its slots — manage it from the Bookings list instead of dragging it here."
                : "This booking is already approved — you can copy it, but not move it. Copy it to the new slot instead.",
            { autoClose: 3200 }
        );
    // Booking DETAILS (purpose, school, participants, event flag, slot) may only
    // be changed while the request is still "Under Review". The moment it is
    // approved or rejected it is a decided booking, so the form is closed off —
    // re-timing an approved booking is still possible by dragging it, which
    // deliberately sends it back for review. A split request is excluded too:
    // the form only ever edits one contiguous slot, so opening it would show
    // (and silently drop) just whichever chip was clicked.
    const canEditDetails = React.useCallback(
        (b) =>
            !!b &&
            b.pending &&
            !b.isSplit &&
            isPendingStatus(b.status) &&
            !b._new && // an unsaved paste has no request row to edit yet
            !!b.requestId &&
            !!onEditBooking &&
            canEditBooking(b),
        [canEditBooking, onEditBooking]
    );

    const handlePillClick = (booking, room) => {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        setActiveResizeId(null);
        const group = groupOf(booking.roomId);
        setActiveBooking({ booking, room, group });
        setPopupStep("details");
        setPopupOpen(true);
    };

    // Ordered list of currently-visible room ids (across expanded groups).
    const orderedRoomIds = () => {
        const ids = [];
        groups.forEach((g) => {
            if (!collapsed[g.id]) g.rooms.forEach((r) => ids.push(r.id));
        });
        return ids;
    };
    // Google-Sheets-style range: every booking inside the room×day (Week) or
    // room×time (Day) rectangle spanning the anchor and the shift-clicked block.
    const computeRange = (anchor, target) => {
        const order = orderedRoomIds();
        const ra = order.indexOf(anchor.roomId);
        const rb = order.indexOf(target.roomId);
        if (ra < 0 || rb < 0) return [target.id];
        const rMin = Math.min(ra, rb);
        const rMax = Math.max(ra, rb);
        const roomIn = (rid) => {
            const i = order.indexOf(rid);
            return i >= rMin && i <= rMax;
        };
        if (isWeek) {
            const da = weekDays.findIndex((d) => toIsoDate(d) === anchor.date);
            const db = weekDays.findIndex((d) => toIsoDate(d) === target.date);
            const dMin = Math.min(da, db);
            const dMax = Math.max(da, db);
            return bookings
                .filter((b) => {
                    const di = weekDays.findIndex((d) => toIsoDate(d) === b.date);
                    return roomIn(b.roomId) && di >= dMin && di <= dMax;
                })
                .map((b) => b.id);
        }
        const tMin = Math.min(anchor.start, target.start);
        const tMax = Math.max(anchor.end, target.end);
        return bookings
            .filter((b) => roomIn(b.roomId) && b.date === dayIso && b.start < tMax && b.end > tMin)
            .map((b) => b.id);
    };

    // The keyboard shortcuts below deliberately ignore keystrokes typed into a
    // field, but the dashboard autofocuses its search box — so on load Ctrl+C /
    // Ctrl+V went to the input and the calendar never saw them. Interacting with
    // the grid hands focus back.
    const releaseFieldFocus = () => {
        const el = document.activeElement;
        const tag = (el?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable) {
            el.blur();
        }
    };

    // Plain click = select one · Ctrl/⌘-click = toggle · Shift-click = range.
    const handleBlockClick = (e, booking) => {
        releaseFieldFocus();
        if (justResizedRef.current || justMovedRef.current) return;
        if (activeResizeId === booking.id) return;
        if (e && e.shiftKey && anchorRef.current) {
            setSelectedIds(computeRange(anchorRef.current, booking));
            return; // keep the anchor for further range extends
        }
        if (e && (e.metaKey || e.ctrlKey)) {
            setSelectedIds((prev) =>
                prev.includes(booking.id) ? prev.filter((id) => id !== booking.id) : [...prev, booking.id]
            );
            anchorRef.current = booking;
            return;
        }
        setSelectedIds([booking.id]);
        anchorRef.current = booking;
    };
    // Double-click opens the booking details preview — in Day view as well as
    // Week. Resizing used to own this gesture in Day view, which meant a Day-view
    // block could never be opened; it now has its own trigger (select the block
    // and press R, or the "Resize" button in the hover tooltip) so both the
    // details preview and the drag-edit / copy-paste flows stay available.
    const handleBlockDblClick = (booking, room) => {
        setTip(null);
        // Other people's under-review slots are anonymous (several requests may
        // compete for the same slot), so they have no detail popup. Your own
        // pending request does — you need to be able to open and edit it.
        if (booking.pending && !isOwnBooking(booking)) return;
        handlePillClick(booking, room);
    };

    // Turn on the drag handles for a block (Day view only — Week capsules have no
    // time axis to drag along).
    const beginResizeMode = (booking) => {
        if (isWeek) return;
        // Resizing re-times an existing booking, so it follows the move rule.
        if (!canMoveBooking(booking)) {
            if (canEditBooking(booking)) warnApprovedMove(booking);
            return;
        }
        setTip(null);
        setSelectedIds([booking.id]);
        anchorRef.current = booking;
        setActiveResizeId(booking.id);
    };

    const handleBlockHover = (e, booking, room) => {
        if (dragRef.current || activeResizeId === booking.id) return;
        if (tipHideTimerRef.current) clearTimeout(tipHideTimerRef.current);
        const rect = e.currentTarget.getBoundingClientRect();
        const above = rect.top > 160;
        setTip({
            booking,
            room,
            x: rect.left + rect.width / 2,
            y: above ? rect.top - 10 : rect.bottom + 10,
            placement: above ? "above" : "below",
        });
    };
    const handleBlockLeave = () => {
        if (tipHideTimerRef.current) clearTimeout(tipHideTimerRef.current);
        tipHideTimerRef.current = setTimeout(() => setTip(null), 220);
    };

    const beginResize = (e, booking, edge) => {
        e.preventDefault();
        e.stopPropagation();
        // Snapshot once at gesture start; onResizeMove then mutates freely.
        pushHistory();
        dragRef.current = {
            id: booking.id,
            edge,
            startX: e.clientX,
            origStart: booking.start,
            origEnd: booking.end,
        };
        window.addEventListener("mousemove", onResizeMove);
        window.addEventListener("mouseup", endResize);
    };
    const onResizeMove = (e) => {
        const d = dragRef.current;
        if (!d || !cellWidth) return;
        const deltaHours = (e.clientX - d.startX) / cellWidth;
        const snapped = Math.round(deltaHours / SNAP) * SNAP;
        setBookings((prev) => {
            const cur = prev.find((b) => b.id === d.id);
            if (!cur) return prev;
            const others = prev.filter(
                (b) => b.id !== d.id && b.roomId === cur.roomId && b.date === cur.date && !b.pending
            );
            const leftLimit = others
                .filter((o) => o.end <= d.origStart)
                .reduce((m, o) => Math.max(m, o.end), START_HOUR);
            const rightLimit = others
                .filter((o) => o.start >= d.origEnd)
                .reduce((m, o) => Math.min(m, o.start), END_HOUR);
            return prev.map((b) => {
                if (b.id !== d.id) return b;
                if (d.edge === "left") {
                    return { ...b, start: clamp(d.origStart + snapped, leftLimit, b.end - SNAP) };
                }
                return { ...b, end: clamp(d.origEnd + snapped, b.start + SNAP, rightLimit) };
            });
        });
    };
    const endResize = () => {
        dragRef.current = null;
        justResizedRef.current = true;
        setTimeout(() => (justResizedRef.current = false), 250);
        window.removeEventListener("mousemove", onResizeMove);
        window.removeEventListener("mouseup", endResize);
    };

    const handleSave = () => {
        const { updates, creates } = pendingChanges;
        if (!updates.length && !creates.length) return;
        if (typeof onSave === "function") onSave({ updates, creates });
        else console.log("Save calendar changes:", { updates, creates });
        setActiveResizeId(null);
    };

    useEffect(() => {
        if (activeResizeId == null) return;
        const onDocMouseDown = (e) => {
            if (dragRef.current) return;
            if (!e.target.closest || !e.target.closest(".bookingBlock")) {
                setActiveResizeId(null);
            }
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [activeResizeId]);

    useEffect(() => () => {
        window.removeEventListener("mousemove", onResizeMove);
        window.removeEventListener("mouseup", endResize);
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        if (tipHideTimerRef.current) clearTimeout(tipHideTimerRef.current);
    }, []);

    const goToConfirm = (mode) => {
        setConfirmMode(mode);
        setCancelReason("");
        setPopupStep("confirm");
    };
    const backToDetails = () => setPopupStep("details");
    // Confirm step's primary action. Only "cancel" is wired (admins can cancel a
    // booked slot); it dispatches the cancel via the parent, with the typed
    // reason, and closes.
    const handleConfirmAction = () => {
        if (confirmMode === "cancel" && activeBooking?.booking?.requestId) {
            onCancelBooking?.(activeBooking.booking.requestId, cancelReason.trim());
        }
        closePopup();
    };
    const closePopup = () => {
        setPopupOpen(false);
        clearTimerRef.current = setTimeout(() => {
            setActiveBooking(null);
            setPopupStep("details");
            setCancelReason("");
        }, POPUP_FADE_MS);
    };
    useEffect(() => () => {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    }, []);

    const visibleBookings = React.useMemo(() => {
        const passesStatus = (b) => !filterStatus || b.status === filterStatus;
        // Confirmed bookings are the shared schedule — always visible. Pending
        // requests are hidden from non-admins unless the request is their own.
        const passesVisibility = (b) => !b.pending || canViewPending || isOwnBooking(b);
        const passes = (b) => passesStatus(b) && passesVisibility(b);
        if (isWeek) {
            const weekIso = new Set(weekDays.map(toIsoDate));
            return bookings.filter((b) => weekIso.has(b.date) && passes(b));
        }
        return bookings.filter((b) => b.date === dayIso && passes(b));
    }, [isWeek, weekDays, bookings, filterStatus, dayIso, canViewPending, isOwnBooking]);

    // Week rows grow to fit the busiest day's capsule list (then scroll inside
    // the cell). Computed per room and shared by both panes to keep them aligned.
    const weekRowHeights = React.useMemo(() => {
        const map = {};
        if (!isWeek) return map;
        const perRoomDay = {};
        visibleBookings.forEach((b) => {
            const k = `${b.roomId}|${b.date}`;
            perRoomDay[k] = (perRoomDay[k] || 0) + 1;
        });
        const MIN = ROW_H, MAX = 260, PAD = 14;
        groups.forEach((g) =>
            g.rooms.forEach((r) => {
                let max = 0;
                weekDays.forEach((d) => {
                    const c = perRoomDay[`${r.id}|${toIsoDate(d)}`] || 0;
                    if (c > max) max = c;
                });
                map[r.id] = Math.min(MAX, Math.max(MIN, PAD + max * CAPSULE_H));
            })
        );
        return map;
    }, [isWeek, visibleBookings, groups, weekDays]);
    const rowHeightFor = (room) => (isWeek ? weekRowHeights[room.id] || ROW_H : ROW_H);

    // A grid column is "past" when its slot has already finished — a whole day in
    // Week view, one hour cell in Day view. Past columns are inert: no
    // slide-to-create, no paste target. Bookings sitting on them are untouched,
    // so an old booking can still be selected and copied forward.
    const isPastColumn = React.useCallback(
        (colMeta) => (isWeek ? isPastDay(colMeta) : isPastSlot(dayIso, colMeta + 1)),
        [isWeek, dayIso]
    );

    // The mirror of the above at the far end: how far ahead this viewer may book.
    // Compared as ISO date strings, which sort lexicographically.
    const maxBookableIso = React.useMemo(
        () => (maxBookingDate ? toIsoDate(maxBookingDate) : null),
        [maxBookingDate]
    );
    const isBeyondDay = React.useCallback(
        (dateIso) => !!maxBookableIso && !!dateIso && dateIso > maxBookableIso,
        [maxBookableIso]
    );
    // In Day view every cell shares the board's date, so the whole day is either
    // in or out; in Week view the column IS the date.
    const isBeyondColumn = React.useCallback(
        (colMeta) => isBeyondDay(isWeek ? colMeta : dayIso),
        [isWeek, dayIso, isBeyondDay]
    );

    const hasConflict = (roomId, dateIso, start, end, excludeId) =>
        bookings.some(
            (b) =>
                !b.pending &&
                b.roomId === roomId &&
                b.date === dateIso &&
                b.id !== excludeId &&
                start < b.end &&
                end > b.start
        );

    // Same check, but also against blocks staged in THIS pass. A multi-block
    // paste adds several rows in one tick, so `bookings` is still the pre-paste
    // array for every block after the first — without `extra`, two pasted blocks
    // could be dropped on top of each other.
    const clashesWith = (dest, extra = []) =>
        [...bookings, ...extra].some(
            (b) =>
                !b.pending &&
                b.roomId === dest.roomId &&
                b.date === dest.date &&
                dest.start < b.end &&
                dest.end > b.start
        );

    // A local, unsaved duplicate of `booking` at `dest`. The `_source*` ids tell
    // the save which server row it must be duplicated from: a pending block has
    // no booking row — its `id` is the synthetic "req-<uuid>" key — so it is
    // duplicated from the request instead.
    const buildCopy = (booking, dest) => ({
        ...booking,
        id: `tmp-${++tmpIdRef.current}`,
        roomId: dest.roomId,
        date: dest.date,
        start: dest.start,
        end: dest.end,
        pending: false,
        _new: true,
        _sourceId: booking._sourceId || (booking.pending ? null : booking.id),
        _sourceRequestId: booking._sourceRequestId || (booking.pending ? booking.requestId : null),
    });

    // Stage a batch of copies in ONE state update, skipping the ones whose target
    // is taken or already over, and report what was dropped — a paste that
    // silently does nothing is indistinguishable from a broken paste.
    const pasteCopies = (pairs) => {
        const additions = [];
        pairs.forEach(({ booking, dest }) => {
            if (isPastSlot(dest.date, dest.end)) return;
            if (isBeyondDay(dest.date)) return;
            if (clashesWith(dest, additions)) return;
            additions.push(buildCopy(booking, dest));
        });
        if (additions.length) {
            setBookings((prev) => [...prev, ...additions]);
            setSelectedIds(additions.map((b) => b.id));
        }
        const skipped = pairs.length - additions.length;
        if (skipped) {
            toast.error(
                additions.length
                    ? `${skipped} of ${pairs.length} bookings couldn't be pasted — those slots are taken or in the past.`
                    : "Nothing could be pasted — those slots are already taken or in the past.",
                { autoClose: 3000 }
            );
        }
        return additions.length;
    };

    // A block cut in one week and pasted into another crosses a refetch, and the
    // baseline that comes back is the server truth for the dates ON SCREEN only —
    // it has no row for a booking that lives in the week we came from. Pasting it
    // here would then diff against nothing, so the move produced no pending change
    // and "Save Changes" stayed disabled even though the block had visibly moved.
    // Re-inserting a cut block therefore carries its ORIGINAL into the baseline,
    // which is what makes the paste read as a move of that server row. Cleared on
    // the next refetch along with the rest of the baseline.
    const rememberMovedInOrigins = (originals) => {
        const list = (originals || []).filter(Boolean);
        if (!list.length) return;
        setBaseline((prev) => {
            const have = new Set(prev.map((o) => o.id));
            const fresh = list.filter((o) => !have.has(o.id));
            return fresh.length ? [...prev, ...fresh] : prev;
        });
    };

    const destinationFor = (booking, roomId, colMeta) => {
        const duration = booking.end - booking.start;
        if (isWeek) return { roomId, date: colMeta, start: booking.start, end: booking.end };
        const start = clamp(colMeta, START_HOUR, END_HOUR - duration);
        return { roomId, date: dayIso, start, end: start + duration };
    };

    // Pasting keeps the copied block's ORIGINAL start/end time — the target cell
    // only supplies the room and (in Week view) the day. Clicking a slot picks
    // WHERE, not WHEN; unlike a drag-move it never re-times the booking.
    const pasteDestinationFor = (booking, roomId, colMeta) => ({
        roomId,
        date: isWeek ? colMeta : dayIso,
        start: booking.start,
        end: booking.end,
    });

    const commitTransfer = (booking, dest, copy, silent = false) => {
        const excludeId = copy ? undefined : booking.id;
        if (isPastSlot(dest.date, dest.end)) {
            if (!silent) {
                toast.error("You can't book a slot in the past.", {
                    autoClose: 2500,
                });
            }
            return false;
        }
        if (isBeyondDay(dest.date)) {
            if (!silent) toast.error(maxBookingDateMessage, { autoClose: 2500 });
            return false;
        }
        if (hasConflict(dest.roomId, dest.date, dest.start, dest.end, excludeId)) {
            if (!silent) {
                toast.error("That room is already booked for this time.", {
                    autoClose: 2500,
                });
            }
            return false;
        }
        if (copy) {
            const block = buildCopy(booking, dest);
            setBookings((prev) => [...prev, block]);
            setSelectedIds([block.id]);
        } else {
            setBookings((prev) =>
                prev.map((b) =>
                    b.id === booking.id
                        ? { ...b, roomId: dest.roomId, date: dest.date, start: dest.start, end: dest.end }
                        : b
                )
            );
        }
        return true;
    };

    const cellKeyOf = (c) => cellKeyFor(c.roomId, isWeek, c.meta);

    // Range of target cells (rooms × days in Week, rooms × hours in Day) between
    // the anchor and the shift-clicked cell — pick a block of days to paste onto.
    const computeCellRange = (anchor, target) => {
        const order = orderedRoomIds();
        const ra = order.indexOf(anchor.roomId);
        const rb = order.indexOf(target.roomId);
        if (ra < 0 || rb < 0) return [target];
        const rMin = Math.min(ra, rb);
        const rMax = Math.max(ra, rb);
        const cells = [];
        if (isWeek) {
            const da = weekDays.findIndex((d) => toIsoDate(d) === anchor.meta);
            const db = weekDays.findIndex((d) => toIsoDate(d) === target.meta);
            const dMin = Math.min(da, db);
            const dMax = Math.max(da, db);
            for (let ri = rMin; ri <= rMax; ri++)
                for (let di = dMin; di <= dMax; di++)
                    cells.push({ roomId: order[ri], meta: toIsoDate(weekDays[di]) });
        } else {
            const ha = HOURS.indexOf(anchor.meta);
            const hb = HOURS.indexOf(target.meta);
            const hMin = Math.min(ha, hb);
            const hMax = Math.max(ha, hb);
            for (let ri = rMin; ri <= rMax; ri++)
                for (let hi = hMin; hi <= hMax; hi++)
                    cells.push({ roomId: order[ri], meta: HOURS[hi] });
        }
        return cells;
    };

    // Plain click = single target · Ctrl/⌘-click = toggle · Shift-click = range.
    const handleCellClick = (e, roomId, colMeta) => {
        releaseFieldFocus();
        if (isPastColumn(colMeta)) return; // nothing can be pasted into the past
        if (isBeyondColumn(colMeta)) return; // ...nor past the booking window
        const cell = { roomId, meta: colMeta };
        const key = cellKeyOf(cell);
        if (e && e.shiftKey && cellAnchorRef.current) {
            setSelectedCells(computeCellRange(cellAnchorRef.current, cell));
            return;
        }
        if (e && (e.metaKey || e.ctrlKey)) {
            setSelectedCells((prev) =>
                prev.some((c) => cellKeyOf(c) === key) ? prev.filter((c) => cellKeyOf(c) !== key) : [...prev, cell]
            );
            cellAnchorRef.current = cell;
            return;
        }
        setSelectedCells([cell]);
        cellAnchorRef.current = cell;
    };

    const selectedCellKeys = selectedCells.map(cellKeyOf);

    const beginMoveDrag = (e, booking) => {
        if (!canEditBooking(booking)) return;
        if (e.button !== 0 || activeResizeId === booking.id) return;
        e.preventDefault();
        moveDragRef.current = { booking, startX: e.clientX, startY: e.clientY, copy: e.altKey, dragging: false };
        window.addEventListener("mousemove", onMoveDrag);
        window.addEventListener("mouseup", endMoveDrag);
    };
    const onMoveDrag = (e) => {
        const d = moveDragRef.current;
        if (!d) return;
        if (!d.dragging) {
            if (Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) < 5) return;
            d.dragging = true;
            setTip(null);
            setSelectedIds([d.booking.id]);
            anchorRef.current = d.booking;
        }
        setGhost({ x: e.clientX, y: e.clientY, label: d.booking.title || "Booking", copy: e.altKey });
    };
    const endMoveDrag = (e) => {
        const d = moveDragRef.current;
        moveDragRef.current = null;
        window.removeEventListener("mousemove", onMoveDrag);
        window.removeEventListener("mouseup", endMoveDrag);
        setGhost(null);
        if (!d || !d.dragging) return;
        justMovedRef.current = true;
        setTimeout(() => (justMovedRef.current = false), 250);

        const el = document.elementFromPoint(e.clientX, e.clientY);
        const rowEl = el && el.closest ? el.closest("[data-roomrow]") : null;
        if (!rowEl) return;
        const roomId = Number(rowEl.getAttribute("data-roomid"));
        const rect = rowEl.getBoundingClientRect();
        const colCount = isWeek ? weekDays.length : HOURS.length;
        const w = rect.width / colCount;
        const colIdx = clamp(Math.floor((e.clientX - rect.left) / w), 0, colCount - 1);
        const colMeta = isWeek ? toIsoDate(weekDays[colIdx]) : HOURS[colIdx];
        // Alt held = drop a copy; otherwise this is a move, which an approved
        // booking only allows for an admin. The drag itself is permitted either
        // way so Alt-dragging a copy off an approved booking still works.
        const isCopy = d.copy || e.altKey;
        if (!isCopy && !canMoveBooking(d.booking)) {
            warnApprovedMove(d.booking);
            return;
        }
        pushHistory();
        commitTransfer(d.booking, destinationFor(d.booking, roomId, colMeta), isCopy);
    };

    // ---- Slide-to-create: 15-min slots (Day) or whole days (Week) ----
    const onCellSelectMove = (e) => {
        const d = slideRef.current;
        if (!d) return;
        const xRel = e.clientX - d.rectLeft;
        if (d.week) {
            const idx = clamp(
                Math.floor(xRel / d.colW),
                d.minIdx || 0,
                d.maxIdx != null ? d.maxIdx : weekDays.length - 1
            );
            if (idx !== d.anchor) d.moved = true;
            d.cursor = idx;
            setDragSelect({ roomId: d.roomId, week: true, a: d.anchor, b: idx });
        } else {
            let cur = START_HOUR + xRel / cellWidth;
            cur = Math.round(cur / SNAP) * SNAP;
            cur = clamp(cur, d.leftLimit, d.rightLimit);
            if (Math.abs(cur - d.anchor) >= SNAP) d.moved = true;
            d.cursor = cur;
            setDragSelect({ roomId: d.roomId, week: false, a: Math.min(d.anchor, cur), b: Math.max(d.anchor, cur) });
        }
    };
    const endCellSelect = () => {
        const d = slideRef.current;
        slideRef.current = null;
        window.removeEventListener("mousemove", onCellSelectMove);
        window.removeEventListener("mouseup", endCellSelect);
        setDragSelect(null);
        if (!d || !d.moved) return;
        if (d.week) {
            const a = Math.min(d.anchor, d.cursor);
            const b = Math.max(d.anchor, d.cursor);
            // Drop days that are already over — selecting Mon–Fri mid-week should
            // still create the days that remain, not fail outright. Days past the
            // booking window drop out the same way.
            const dates = [];
            let droppedBeyond = false;
            for (let i = a; i <= b; i++) {
                const iso = toIsoDate(weekDays[i]);
                if (isPastDay(iso)) continue;
                if (isBeyondDay(iso)) { droppedBeyond = true; continue; }
                dates.push(iso);
            }
            if (!dates.length) {
                toast.error(
                    droppedBeyond ? maxBookingDateMessage : "You can't book a slot in the past.",
                    { autoClose: 2500 }
                );
                return;
            }
            setCreateSel({ roomId: d.roomId, roomName: roomNameFor(d.roomId), dates, start: 9, end: 10 });
            setCreateOpen(true);
        } else {
            let s = Math.min(d.anchor, d.cursor);
            let en = Math.max(d.anchor, d.cursor);
            if (en - s < SNAP) en = s + SNAP;
            if (isPastSlot(dayIso, en)) {
                toast.error("You can't book a slot in the past.", {
                    autoClose: 2500,
                });
                return;
            }
            if (isBeyondDay(dayIso)) {
                toast.error(maxBookingDateMessage, { autoClose: 2500 });
                return;
            }
            if (hasConflict(d.roomId, dayIso, s, en)) {
                toast.error("That room is already booked for this time.", {
                    autoClose: 2500,
                });
                return;
            }
            setCreateSel({ roomId: d.roomId, roomName: roomNameFor(d.roomId), date: dayIso, start: s, end: en });
            setCreateOpen(true);
        }
    };
    const beginCellSelect = (e, room, rowEl) => {
        if (e.button !== 0) return;
        // Ctrl/⌘/Shift-click is a multi-cell paste-target selection, not a slide.
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        if (e.target.closest && e.target.closest(".bookingBlock")) return;
        const rect = rowEl.getBoundingClientRect();
        const xRel = e.clientX - rect.left;
        if (isWeek) {
            const colW = rect.width / weekDays.length;
            const idx = clamp(Math.floor(xRel / colW), 0, weekDays.length - 1);
            // Can't start a selection on a day that's already over, or one past
            // the booking window.
            if (isPastDay(toIsoDate(weekDays[idx]))) return;
            if (isBeyondDay(toIsoDate(weekDays[idx]))) return;
            // …and can't be dragged over either kind. The bookable days form one
            // contiguous run, so a first/last index bounds the drag on both sides.
            const firstBookable = weekDays.findIndex((d) => !isPastDay(toIsoDate(d)));
            const lastBookable = weekDays.reduce(
                (last, d, i) => (isBeyondDay(toIsoDate(d)) ? last : i),
                weekDays.length - 1
            );
            slideRef.current = {
                roomId: room.id, rectLeft: rect.left, colW, week: true,
                anchor: idx, cursor: idx, moved: false,
                minIdx: firstBookable < 0 ? idx : firstBookable,
                maxIdx: lastBookable,
            };
            setDragSelect({ roomId: room.id, week: true, a: idx, b: idx });
        } else {
            let anchor = START_HOUR + xRel / cellWidth;
            anchor = clamp(Math.round(anchor / SNAP) * SNAP, START_HOUR, END_HOUR - SNAP);
            const others = bookings.filter((b) => !b.pending && b.roomId === room.id && b.date === dayIso);
            // Selection can't start in — or be dragged back into — a slot that
            // has already passed.
            const floor = bookableFloor(dayIso);
            if (anchor < floor) return;
            const leftLimit = Math.max(
                floor,
                others.filter((o) => o.end <= anchor).reduce((m, o) => Math.max(m, o.end), START_HOUR)
            );
            const rightLimit = others.filter((o) => o.start >= anchor).reduce((m, o) => Math.min(m, o.start), END_HOUR);
            if (anchor < leftLimit || anchor >= rightLimit) return;
            slideRef.current = { roomId: room.id, rectLeft: rect.left, week: false, anchor, cursor: anchor, leftLimit, rightLimit, moved: false };
            setDragSelect({ roomId: room.id, week: false, a: anchor, b: anchor });
        }
        window.addEventListener("mousemove", onCellSelectMove);
        window.addEventListener("mouseup", endCellSelect);
    };

    const computeDates = (baseIso, recurrence) => {
        if (!recurrence) return [baseIso];
        const base = fromIsoDate(baseIso);
        const step = recurrence.frequency === "weekly" ? 7 : 1;
        return Array.from({ length: recurrence.count }, (_, i) => toIsoDate(addDays(base, i * step)));
    };

    // Confirm the create popup. Time can be edited in the popup; multi-day (week
    // slide) selections carry their own `dates`, single-day ones expand recurrence.
    const handleCreateConfirm = async ({ meeting_title, attendees, school, needInstructor, isEvent, start, end, recurrence }) => {
        const sel = createSel;
        if (!sel) {
            setCreateOpen(false);
            return;
        }
        const startH = start != null ? start : sel.start;
        const endH = end != null ? end : sel.end;
        const slot = `${fracToHHMM(startH)} - ${fracToHHMM(endH)}`;
        const dates = sel.dates && sel.dates.length ? sel.dates : computeDates(sel.date, recurrence);
        if (typeof onCreateBooking === "function") {
            try {
                // Keep the popup open (showing its loading state, driven by the
                // `creatingBooking` prop) until the parent's create settles, then
                // close on success. On failure, leave the popup so the admin can
                // retry without re-entering everything.
                await onCreateBooking({ room_id: sel.roomId, dates, slot: [slot], meeting_title, attendees, school, needInstructor, isEvent });
            } catch (e) {
                return; // keep popup open on failure
            }
        }
        setCreateOpen(false);
        setCreateSel(null);
        setDragSelect(null);
    };

    // Open the create form as an EDIT form, prefilled from the request. The
    // details popup (if it's what we came from) closes first so only one modal
    // is ever on screen; `editSel` holds everything the form needs, so losing
    // `activeBooking` to that close is fine.
    const openEditDetails = (booking) => {
        if (!canEditDetails(booking)) return;
        setTip(null);
        setEditSel({
            requestId: booking.requestId,
            roomId: booking.roomId,
            roomName: roomNameFor(booking.roomId),
            date: booking.date,
            start: booking.start,
            end: booking.end,
            title: booking.title,
            school: booking.schoolKey,
            attendees: booking.attendees || [],
            isEvent: !!booking.isEvent,
            needInstructor: !!booking.needInstructor,
        });
        if (popupOpen) closePopup();
        setEditOpen(true);
    };

    // Save the edited details. Mirrors handleCreateConfirm: the popup stays open
    // (showing its loading state) until the parent's update settles, so a
    // failure leaves the form filled in for another go.
    const handleEditConfirm = async ({ meeting_title, attendees, school, needInstructor, isEvent, start, end }) => {
        const sel = editSel;
        if (!sel) {
            setEditOpen(false);
            return;
        }
        const startH = start != null ? start : sel.start;
        const endH = end != null ? end : sel.end;
        try {
            await onEditBooking({
                requestId: sel.requestId,
                room_id: sel.roomId,
                date: sel.date,
                slot: [`${fracToHHMM(startH)} - ${fracToHHMM(endH)}`],
                meeting_title,
                attendees,
                school,
                needInstructor,
                isEvent,
            });
        } catch (e) {
            return; // keep the popup open on failure
        }
        setEditOpen(false);
        setEditSel(null);
    };

    const handleRecurConfirm = ({ recurrence }) => {
        setRecurOpen(false);
        const b = recurBooking;
        if (!b || !recurrence) return;
        const dates = computeDates(b.date, recurrence).slice(1);
        pushHistory();
        dates.forEach((iso) => {
            commitTransfer(b, { roomId: b.roomId, date: iso, start: b.start, end: b.end }, true, true);
        });
        setRecurBooking(null);
    };

    // ---- Copy the SELECTED bookings and paste them onto another week ----
    // Snapshots each selected (approved) block by its weekday offset and its
    // room offset *relative to the top-most selected room* — the room id itself
    // isn't kept fixed, so the paste can land in any room the admin targets.
    const copySelection = (mode = "copy") => {
        const order = orderedRoomIds();
        const start = startOfWeek(date);
        // Confirmed bookings AND the user's own under-review requests can be
        // copied — both resolve to a server row the copy is duplicated from. Only
        // blocks the user is allowed to edit, and not an unsaved copy.
        const selected = bookings.filter((b) => selectedIds.includes(b.id) && !b._new);
        const editable = selected.filter(canEditBooking);
        // A cut is a move, so an approved booking drops out of the selection —
        // it can still be copied. Called out rather than silently narrowed.
        const chosen = mode === "cut" ? editable.filter(canMoveBooking) : editable;
        if (!chosen.length) {
            toast.error(
                editable.length && mode === "cut"
                    ? "Those bookings are already approved — you can copy them, but not move them."
                    : selected.length
                        ? "You can only copy your own bookings."
                        : "Select a booking first.",
                { autoClose: 3200 }
            );
            return;
        }
        if (mode === "cut" && chosen.length < editable.length) {
            toast.info(
                `${editable.length - chosen.length} approved booking${editable.length - chosen.length > 1 ? "s were" : " was"} left in place — approved bookings can only be copied.`,
                { autoClose: 3200 }
            );
        }
        const roomIdxs = chosen.map((b) => order.indexOf(b.roomId)).filter((i) => i >= 0);
        const minIdx = roomIdxs.length ? Math.min(...roomIdxs) : 0;
        const items = chosen.map((b) => ({
            booking: { ...b }, // the original block, re-inserted at the target on a cut
            dow: clamp(Math.round((fromIsoDate(b.date) - start) / 86400000), 0, 6),
            roomId: b.roomId, // fallback: paste in place when no target room chosen
            roomOffset: Math.max(0, order.indexOf(b.roomId) - minIdx),
            start: b.start,
            end: b.end,
        }));
        setClipboard(null); // route Ctrl+V to the selection paste
        setWeekClipboard({ count: items.length, items, mode });
        // A cut removes the blocks immediately (like cutting text); they come
        // back when pasted, or are restored on Esc.
        if (mode === "cut") {
            pushHistory();
            const cutIds = chosen.map((b) => b.id);
            setBookings((prev) => prev.filter((b) => !cutIds.includes(b.id)));
            setSelectedIds([]);
        }
    };

    // Paste the copied/cut selection onto the current week (Week view) or the
    // current day (Day view). A copy duplicates the blocks; a cut MOVES the
    // originals (no duplicate). Selected target cells receive one block each (in
    // reading order). Timings are always preserved — a target cell picks WHERE a
    // block goes, never WHEN, exactly like the single-booking paste.
    const handlePasteSelection = () => {
        if (!weekClipboard || !weekClipboard.items.length) return;
        pushHistory();
        const isCut = weekClipboard.mode === "cut";
        const order = orderedRoomIds();
        const start = startOfWeek(date);

        // Pair copied blocks with selected cells in READING order (top row →
        // bottom, then left → right along the columns: days in Week view, hours
        // in Day view), so the mapping is predictable no matter what order the
        // cells were clicked in.
        const colIdxOf = (meta) =>
            isWeek ? weekDays.findIndex((d) => toIsoDate(d) === meta) : HOURS.indexOf(meta);
        const cells = [...selectedCells].sort((a, b) => {
            const ra = order.indexOf(a.roomId);
            const rb = order.indexOf(b.roomId);
            return ra !== rb ? ra - rb : colIdxOf(a.meta) - colIdxOf(b.meta);
        });
        const items = [...weekClipboard.items].sort((a, b) =>
            a.roomOffset !== b.roomOffset ? a.roomOffset - b.roomOffset : a.dow - b.dow
        );

        // Where each copied block lands:
        //   • a target cell is selected → drop the block onto that cell's room,
        //     and (Week view only) that cell's day — one block per cell, in
        //     reading order. In Day view every block stays on the day on screen:
        //     an hour cell carries no date, so its `meta` must never be used as
        //     one.
        //   • leftover blocks (more blocks than cells) — in Day view they pile
        //     into the LAST targeted room, which is what "paste these onto that
        //     room" means when a single cell is clicked. In Week view they keep
        //     their original room + copied weekday, since spreading them over one
        //     day would just stack them on top of each other.
        //   • nothing selected → back to the original room, on the copied weekday
        //     (Week) or the day on screen (Day), so a cut never loses a block.
        // Timings (start/end) are ALWAYS preserved; only the room/day move.
        const destFor = (it, i) => {
            const cell = cells[i] || (isWeek ? null : cells[cells.length - 1]);
            if (cell) {
                return { roomId: cell.roomId, date: isWeek ? cell.meta : dayIso, start: it.start, end: it.end };
            }
            return {
                roomId: it.roomId,
                date: isWeek ? toIsoDate(addDays(start, it.dow)) : dayIso,
                start: it.start,
                end: it.end,
            };
        };

        if (isCut) {
            // Re-insert the removed blocks at their targets (a real move, no copy).
            const additions = [];
            const origins = [];
            items.forEach((it, i) => {
                const dest = destFor(it, i);
                if (isPastSlot(dest.date, dest.end)) return;
                if (isBeyondDay(dest.date)) return;
                if (clashesWith(dest, additions)) return;
                additions.push({ ...it.booking, roomId: dest.roomId, date: dest.date, start: dest.start, end: dest.end });
                origins.push(it.booking); // where it came from, for the Save diff
            });
            if (additions.length) {
                rememberMovedInOrigins(origins);
                setBookings((prev) => [...prev, ...additions]);
            }
            const skipped = items.length - additions.length;
            if (skipped) {
                toast.error(
                    additions.length
                        ? `${skipped} of ${items.length} bookings couldn't be moved — those slots are taken or in the past.`
                        : "Nothing could be moved — those slots are already taken or in the past.",
                    { autoClose: 3000 }
                );
            }
            setWeekClipboard(null);
            return;
        }

        // Copy from the ORIGINAL block so the duplicate resolves the right server
        // source (booking id, or request id for a pending one) and keeps its
        // colour / title / school.
        pasteCopies(items.map((it, i) => ({ booking: it.booking, dest: destFor(it, i) })));
    };

    useEffect(() => {
        // Put back any blocks removed by an un-pasted cut (single or selection).
        const restorePendingCut = () => {
            const add = [];
            if (clipboard?.mode === "cut" && clipboard.booking) add.push(clipboard.booking);
            if (weekClipboard?.mode === "cut") (weekClipboard.items || []).forEach((it) => it.booking && add.push(it.booking));
            if (add.length) {
                setBookings((prev) => {
                    const have = new Set(prev.map((b) => b.id));
                    const fresh = add.filter((b) => !have.has(b.id));
                    return fresh.length ? [...prev, ...fresh] : prev;
                });
            }
        };
        const onKey = (e) => {
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable || popupOpen || createOpen || recurOpen || editOpen) return;
            const mod = e.metaKey || e.ctrlKey;
            const k = (e.key || "").toLowerCase();
            if (mod && (k === "c" || k === "x") && selectedIds.length > 1) {
                // Multiple blocks selected → snapshot them; X = cut (remove now), C = copy.
                e.preventDefault();
                restorePendingCut();
                copySelection(k === "x" ? "cut" : "copy");
            } else if (mod && (k === "c" || k === "x")) {
                const booking = bookings.find((b) => b.id === selectedId);
                if (!booking) return;
                if (!canEditBooking(booking)) {
                    toast.error("You can only copy your own bookings.", {
                        autoClose: 2500,
                    });
                    return;
                }
                // Cutting is a move — an approved booking may only be copied.
                if (k === "x" && !canMoveBooking(booking)) {
                    e.preventDefault();
                    warnApprovedMove(booking);
                    return;
                }
                e.preventDefault();
                restorePendingCut();
                setWeekClipboard(null); // switch to single-booking clipboard
                if (k === "x") {
                    // Cut removes the block immediately (like cutting text).
                    pushHistory();
                    setClipboard({ booking: { ...booking }, mode: "cut" });
                    setBookings((prev) => prev.filter((b) => b.id !== booking.id));
                    setSelectedIds([]);
                } else {
                    setClipboard({ booking, mode: "copy" });
                }
            } else if (mod && k === "v") {
                // A copied/cut selection pastes onto the current week (by weekday)
                // or, in Day view, onto the targeted room(s) on the day on screen.
                if (weekClipboard) {
                    e.preventDefault();
                    handlePasteSelection();
                    return;
                }
                if (!clipboard) return;
                if (!selectedCells.length) return;
                e.preventDefault();
                if (clipboard.mode === "cut") {
                    // Re-insert the removed block at the first target cell (a move).
                    const b = clipboard.booking;
                    const c = selectedCells[0];
                    const dest = pasteDestinationFor(b, c.roomId, c.meta);
                    if (hasConflict(dest.roomId, dest.date, dest.start, dest.end)) {
                        toast.error("That room is already booked for this time.", { autoClose: 2500 });
                        return;
                    }
                    pushHistory();
                    rememberMovedInOrigins([b]); // b still holds the pre-cut slot
                    setBookings((prev) => [...prev, { ...b, roomId: dest.roomId, date: dest.date, start: dest.start, end: dest.end }]);
                    setSelectedIds([b.id]);
                    setClipboard(null);
                } else {
                    // Copy → stamp onto every selected day/cell, in one pass so
                    // two targets can't be handed the same slot.
                    pushHistory();
                    pasteCopies(
                        selectedCells.map((c) => ({
                            booking: clipboard.booking,
                            dest: pasteDestinationFor(clipboard.booking, c.roomId, c.meta),
                        }))
                    );
                }
            } else if (mod && k === "z") {
                // Ctrl/⌘+Z = undo, Ctrl/⌘+Shift+Z = redo (also Ctrl+Y).
                e.preventDefault();
                if (e.shiftKey) redo();
                else undo();
            } else if (mod && k === "y") {
                e.preventDefault();
                redo();
            } else if (!mod && k === "r") {
                // Resize handles for the selected block (Day view). Double-click
                // is the details preview now, so resizing gets its own trigger.
                const booking = bookings.find((b) => b.id === selectedId);
                if (!canEditBooking(booking) || isWeek) return;
                e.preventDefault();
                beginResizeMode(booking); // re-checks the move rule, warns if approved
            } else if (k === "escape") {
                restorePendingCut(); // un-pasted cut → put the blocks back
                setActiveResizeId(null);
                setClipboard(null);
                setWeekClipboard(null);
                setSelectedCells([]);
                cellAnchorRef.current = null;
                setSelectedIds([]);
                anchorRef.current = null;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, selectedIds, selectedCells, clipboard, weekClipboard, bookings, isWeek, popupOpen, createOpen, recurOpen, editOpen, dayIso, past, future, canEditBooking, canMoveBooking]);

    const toggleGroup = (id) =>
        setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
    const toggleDay = (iso) => setExpandedDay((cur) => (cur === iso ? null : iso));

    const titleText = view === "Week"
        ? `Booking Chart · Week of ${weekDays[0].toLocaleDateString("en-US", { month: "long", day: "numeric" })}`
        : `Booking Chart · ${formatLongDate(date)}`;

    // The 7 weekday cells (flex-filled, no horizontal scroll) with an expand
    // arrow. Reused for the inline Week header and the static strip shown while a
    // day is expanded.
    const dayStripCells = showWeekStrip
        ? weekDays.map((day) => {
            const iso = toIsoDate(day);
            const isOpen = expandedDay === iso;
            return (
                <TimeCell
                    key={iso}
                    header
                    justiCenter
                    alignCenter
                    onClick={() => toggleDay(iso)}
                    style={{ flex: "1 1 0", minWidth: 0, height: "100%", cursor: "pointer", gap: 6, background: isOpen ? "rgba(80,120,255,0.12)" : undefined }}
                >
                    <Text font10 inter bold black4>
                        {formatDayHeader(day)}
                    </Text>
                    <span style={{ display: "inline-flex", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                        <AngleDown />
                    </span>
                </TimeCell>
            );
        })
        : null;

    // Section header chevron used in both panes.
    const SectionChevron = ({ expanded }) => (
        <Flexbox
            maxWidth={"max-content"}
            alignCenter
            padding='5px'
            justiCenter
            borderRadius={40}
            style={{
                border: `1px solid ${globalTheme.colors.nuetral2}`,
                background: globalTheme.colors.white1,
                transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.2s ease",
            }}
        >
            <AngleDown />
        </Flexbox>
    );

    return (
        <WrapperBox padding={"0"} borderRadius={10} borderColor={globalTheme.colors.nuetral2} overflow={"visible"}>
            {/* Title bar */}
            <Flexbox spaceBW alignCenter padding={"16px 20px"}>
                <Flexbox column gap={2}>
                    <Text font14 inter semiBold black3>
                        {titleText}
                    </Text>
                    <Text font11 inter medium nuetral6>
                        Slide empty slots to create · double-click a booking for details · Ctrl/⌘-click to multi-select · Shift-click for a range · Ctrl/⌘+C / X then click a target cell and Ctrl/⌘+V · drag to move · select and press R to resize
                    </Text>
                    {clipboard && (
                        <Text font11 inter semiBold style={{ color: "rgba(80,120,255,1)" }}>
                            {clipboard.mode === "cut" ? "Cut" : "Copied"}: {clipboard.booking?.title || "Booking"} — click a target cell, then Ctrl/⌘+V
                        </Text>
                    )}
                    {weekClipboard && (
                        <Text font11 inter semiBold style={{ color: "rgba(80,120,255,1)" }}>
                            {weekClipboard.mode === "cut" ? "Cut" : "Copied"} {weekClipboard.count} booking{weekClipboard.count > 1 ? "s" : ""} — go to the target {isWeek ? "week" : "day"}, click {isWeek ? "the target cell(s)" : "a cell in the target room"} and press Ctrl/⌘+V (or click “{weekClipboard.mode === "cut" ? "Move" : "Paste"}”).
                        </Text>
                    )}
                </Flexbox>

                <Flexbox gap={10} maxWidth="max-content" alignCenter>
                    {selectedIds.length > 0 && (
                        <Button
                            buttonMd
                            name={`Copy Selected (${selectedIds.length})`}
                            customPadding="8px 16px"
                            // Wrapped: onClick would otherwise hand the click
                            // event in as `mode`, parking a SyntheticEvent in
                            // the clipboard where "copy"/"cut" belongs.
                            onClick={() => copySelection("copy")}
                            style={{
                                whiteSpace: "nowrap",
                                background: globalTheme.colors.white1,
                                border: `1px solid ${globalTheme.colors.nuetral2}`,
                                color: globalTheme.colors.black4,
                            }}
                        />
                    )}
                    {weekClipboard && (
                        <Button
                            buttonMd
                            name={`${weekClipboard.mode === "cut" ? "Move" : "Paste"} (${weekClipboard.count})`}
                            customPadding="8px 16px"
                            onClick={handlePasteSelection}
                            style={{
                                whiteSpace: "nowrap",
                                background: globalTheme.colors.white1,
                                border: `1px solid ${globalTheme.colors.nuetral2}`,
                                color: globalTheme.colors.black4,
                            }}
                        />
                    )}
                    {/* {selectedBooking && !selectedBooking.pending && canEditBooking(selectedBooking) && (
                        <Button
                            buttonMd
                            name="Make recurring"
                            customPadding="8px 16px"
                            onClick={() => {
                                setRecurBooking(selectedBooking);
                                setRecurOpen(true);
                            }}
                            style={{
                                whiteSpace: "nowrap",
                                background: globalTheme.colors.white1,
                                border: `1px solid ${globalTheme.colors.nuetral2}`,
                                color: globalTheme.colors.black4,
                            }}
                        />
                    )} */}
                    <Button
                        buttonMd
                        name="Undo"
                        customPadding="8px 14px"
                        onClick={undo}
                        title="Undo (Ctrl/⌘+Z)"
                        style={{
                            whiteSpace: "nowrap",
                            background: globalTheme.colors.white1,
                            border: `1px solid ${globalTheme.colors.nuetral2}`,
                            color: globalTheme.colors.black4,
                            opacity: canUndo ? 1 : 0.4,
                            pointerEvents: canUndo ? "auto" : "none",
                        }}
                    />
                    <Button
                        buttonMd
                        name="Redo"
                        customPadding="8px 14px"
                        onClick={redo}
                        title="Redo (Ctrl/⌘+Shift+Z)"
                        style={{
                            whiteSpace: "nowrap",
                            background: globalTheme.colors.white1,
                            border: `1px solid ${globalTheme.colors.nuetral2}`,
                            color: globalTheme.colors.black4,
                            opacity: canRedo ? 1 : 0.4,
                            pointerEvents: canRedo ? "auto" : "none",
                        }}
                    />
                    <Button
                        buttonMd
                        primaryFilled
                        revIcon
                        name={dirty ? `Save Changes (${pendingCount})` : "Save Changes"}
                        customPadding="8px 16px"
                        onClick={handleSave}
                        style={{
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            opacity: dirty ? 1 : 0.5,
                            pointerEvents: dirty ? "auto" : "none",
                        }}
                    />
                </Flexbox>
            </Flexbox>

            {/* When a weekday is expanded, the day strip stays a static full-width
                nav bar (it never scrolls) while only the hourly grid below scrolls. */}
            {showWeekStrip && expandedDay && (
                <Flexbox noWrap style={{ borderTop: "1px solid #eee" }}>
                    <RoomCol header alignCenter padding="0 16px" style={{ height: STRIP_H, boxSizing: "border-box", flex: `0 0 ${LEFT_COL_WIDTH}px`, width: LEFT_COL_WIDTH, background: globalTheme.colors.nuetral2 }}>
                        <Text font11 inter medium black4 uppercase>
                            ROOM · CAPACITY
                        </Text>
                    </RoomCol>
                    <div style={{ display: "flex", flex: "1 1 0", minWidth: 0, height: STRIP_H }}>{dayStripCells}</div>
                </Flexbox>
            )}

            {/* Scrollable grid — single scroll container; the left pane is frozen via
                position:sticky;left:0 so room names stay put while the hour columns
                scroll horizontally (Week days flex to fill, so they never scroll). */}
            <div
                ref={gridScrollRef}
                onScroll={handleGridScroll}
                style={{ display: "flex", maxHeight: "calc(100vh - 330px)", overflow: "auto", borderTop: "1px solid #eee" }}
            >
                {/* LEFT PANE (frozen) */}
                <div style={{ position: "sticky", left: 0, zIndex: 8, flex: `0 0 ${LEFT_COL_WIDTH}px`, width: LEFT_COL_WIDTH, background: globalTheme.colors.white1, borderRight: "1px solid #eee" }}>
                    <div style={{ position: "sticky", top: 0, zIndex: 9, background: globalTheme.colors.nuetral2 }}>
                        {isWeek && (
                            <RoomCol header alignCenter padding="0 16px" style={{ height: STRIP_H, boxSizing: "border-box" }}>
                                <Text font11 inter medium black4 uppercase>
                                    ROOM · CAPACITY
                                </Text>
                            </RoomCol>
                        )}
                        {!isWeek && (
                            <RoomCol header alignCenter padding="0 16px" style={{ height: HDR_H, boxSizing: "border-box" }}>
                                <Text font11 inter medium black4 uppercase>
                                    {expandedDay ? formatDayHeader(activeDate) : "ROOM · CAPACITY"}
                                </Text>
                            </RoomCol>
                        )}
                    </div>
                    {showInitialSkeleton
                        ? Array.from({ length: INITIAL_SKELETON_ROWS }).map((_, i) => (
                            <RoomNameSkeletonCell key={`sk-name-${i}`} />
                        ))
                        : groups.length === 0 && <div style={{ height: 96 }} />}
                    {groups.map((group) => {
                        const expanded = !collapsed[group.id];
                        const totalRooms = group.rooms.length;
                        const bookedRooms = new Set(
                            visibleBookings.filter((b) => group.rooms.some((r) => r.id === b.roomId)).map((b) => b.roomId)
                        ).size;
                        return (
                            <React.Fragment key={group.id}>
                                <Flexbox
                                    alignCenter
                                    spaceBW
                                    background={globalTheme.colors.grey6}
                                    padding={"0 16px"}
                                    onClick={() => toggleGroup(group.id)}
                                    style={{ height: SEC_H, boxSizing: "border-box", borderTop: "1px solid #eee", borderBottom: "1px solid #eee", cursor: "pointer" }}
                                >
                                    <Flexbox column gap={1} maxWidth="max-content">
                                        <Text font12 inter bold black4 uppercase style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>
                                            {group.name}
                                        </Text>
                                        <Text font10 inter medium black3>
                                            {bookedRooms}/{totalRooms} booked
                                        </Text>
                                    </Flexbox>
                                    <SectionChevron expanded={expanded} />
                                </Flexbox>
                                {expanded && group.rooms.map((room) => <RoomNameCell key={room.id} room={room} rowHeight={rowHeightFor(room)} />)}
                            </React.Fragment>
                        );
                    })}
                    {showMoreSkeleton &&
                        Array.from({ length: MORE_SKELETON_ROWS }).map((_, i) => (
                            <RoomNameSkeletonCell key={`sk-more-name-${i}`} />
                        ))}
                </div>

                {/* RIGHT PANE — Week: flex-fills the pane (no horizontal scroll);
                    Day/expanded: fixed width so the hours scroll horizontally. */}
                <div style={isWeek ? { flex: "1 1 0", minWidth: 0 } : { flex: "0 0 auto", width: bodyColsWidth }}>
                    <div style={{ width: isWeek ? "100%" : bodyColsWidth }}>
                        <div style={{ position: "sticky", top: 0, zIndex: 7 }}>
                            {isWeek && (
                                <div style={{ display: "flex", height: STRIP_H }}>{dayStripCells}</div>
                            )}
                            {!isWeek && (
                                <div style={{ display: "flex", height: HDR_H }}>
                                    {HOURS.map((h) => (
                                        <TimeCell
                                            key={h}
                                            header
                                            justiCenter
                                            alignCenter
                                            style={{ width: HOUR_COL_PX, flex: `0 0 ${HOUR_COL_PX}px`, height: "100%" }}
                                        >
                                            <Text font10 inter bold black4>
                                                {formatHour(h)}
                                            </Text>
                                        </TimeCell>
                                    ))}
                                </div>
                            )}
                        </div>

                        {showInitialSkeleton &&
                            Array.from({ length: INITIAL_SKELETON_ROWS }).map((_, i) => (
                                <RoomCellsSkeletonRow key={`sk-row-${i}`} width={isWeek ? "100%" : bodyColsWidth} />
                            ))}

                        {!showInitialSkeleton && groups.length === 0 && (
                            <Flexbox justiCenter alignCenter style={{ height: 96 }}>
                                <Text font13 inter regular nuetral6>
                                    No rooms found for this date
                                </Text>
                            </Flexbox>
                        )}

                        {groups.map((group) => {
                            const expanded = !collapsed[group.id];
                            return (
                                <React.Fragment key={group.id}>
                                    <div
                                        onClick={() => toggleGroup(group.id)}
                                        style={{ height: SEC_H, boxSizing: "border-box", background: globalTheme.colors.grey6, borderTop: "1px solid #eee", borderBottom: "1px solid #eee", cursor: "pointer" }}
                                    />
                                    {expanded &&
                                        group.rooms.map((room) => {
                                            const roomBookings = visibleBookings.filter((b) => b.roomId === room.id);
                                            return (
                                                <RoomCellsRow
                                                    key={room.id}
                                                    room={room}
                                                    bookings={roomBookings}
                                                    isWeek={isWeek}
                                                    weekDays={weekDays}
                                                    cellWidth={cellWidth}
                                                    colPx={colPx}
                                                    onBlockClick={handleBlockClick}
                                                    onBlockDblClick={handleBlockDblClick}
                                                    onBlockHover={handleBlockHover}
                                                    onBlockLeave={handleBlockLeave}
                                                    activeResizeId={activeResizeId}
                                                    onBeginResize={beginResize}
                                                    selectedCellKeys={selectedCellKeys}
                                                    onCellClick={handleCellClick}
                                                    clipboardId={clipboard?.booking?.id}
                                                    selectedIds={selectedIds}
                                                    onBlockMouseDown={beginMoveDrag}
                                                    onRowMouseDown={beginCellSelect}
                                                    dragSelect={dragSelect}
                                                    rowHeight={rowHeightFor(room)}
                                                    canEditBooking={canEditBooking}
                                                    canMoveBooking={canMoveBooking}
                                                    isOwnBooking={isOwnBooking}
                                                    isPastColumn={isPastColumn}
                                                    isBeyondColumn={isBeyondColumn}
                                                    beyondWindowMessage={maxBookingDateMessage}
                                                />
                                            );
                                        })}
                                </React.Fragment>
                            );
                        })}

                        {showMoreSkeleton &&
                            Array.from({ length: MORE_SKELETON_ROWS }).map((_, i) => (
                                <RoomCellsSkeletonRow key={`sk-more-row-${i}`} width={isWeek ? "100%" : bodyColsWidth} />
                            ))}
                    </div>
                </div>
            </div>

            {/* Details / confirm popup */}
            {(() => {
                const detailsProps = {
                    onClose: closePopup,
                    onOverride: () => goToConfirm("override"),
                    onAccept: () => goToConfirm("accept"),
                    onCancel: () => goToConfirm("cancel"),
                    roomName: activeBooking?.room?.name,
                    capacity: activeBooking?.room?.capacity,
                    location: activeBooking?.group?.name,
                    bookingDate: activeBooking?.booking?.date ? formatPopupDate(activeBooking.booking.date) : undefined,
                    // A split booking (gap between its slots) shows every run here,
                    // not just the chip that was opened — see runsForRow.
                    bookingTime: activeBooking?.booking
                        ? activeBooking.booking.runsLabel ||
                          `${formatTime12(activeBooking.booking.start)} - ${formatTime12(activeBooking.booking.end)}`
                        : undefined,
                    duration: activeBooking?.booking
                        ? activeBooking.booking.isSplit
                            ? formatDuration(0, activeBooking.booking.runsTotalHours)
                            : formatDuration(activeBooking.booking.start, activeBooking.booking.end)
                        : undefined,
                    bookedBy: activeBooking?.booking?.bookedBy || "—",
                    description: activeBooking?.booking?.title,
                    // Drives the admin-only dashboard comment thread. Null for a
                    // confirmed booking with no originating request.
                    requestId: activeBooking?.booking?.requestId || null,
                    // Admin color tagging.
                    colorTypes,
                    colorTypeId: activeBooking?.booking?.colorTypeId || null,
                    onChangeColorType: onAssignColorType
                        ? (colorTypeId) =>
                              onAssignColorType(activeBooking?.booking?.requestId, colorTypeId)
                        : null,
                    isClassAdmin : isisClassAdmin,
                    // Details are editable only while the request is under
                    // review; `editLocked` explains the missing button to the
                    // people who could otherwise have used it.
                    canEdit: canEditDetails(activeBooking?.booking),
                    editLocked:
                        !!onEditBooking &&
                        !!activeBooking?.booking &&
                        !activeBooking.booking._new &&
                        !activeBooking.booking.pending &&
                        canEditBooking(activeBooking.booking),
                    splitLocked:
                        !!onEditBooking &&
                        !!activeBooking?.booking?.isSplit &&
                        !!activeBooking.booking.pending &&
                        !activeBooking.booking._new &&
                        canEditBooking(activeBooking.booking),
                    onEdit: () => openEditDetails(activeBooking?.booking),
                };
                const confirmProps = {
                    mode: confirmMode,
                    onCancel: backToDetails,
                    onConfirm: handleConfirmAction,
                    roomName: activeBooking?.room?.name,
                    bookingDate: detailsProps.bookingDate,
                    bookingTime: detailsProps.bookingTime,
                    duration: detailsProps.duration,
                    bookedBy: detailsProps.bookedBy,
                    reason: cancelReason,
                    setReason: setCancelReason,
                };
                const isConfirm = popupStep === "confirm";
                return (
                    <PopupWrapper className={popupOpen ? "active" : ""}>
                        <PopupBody maxWidth={620} padding="0" style={{ overflow: "hidden" }}>
                            <div
                                style={{
                                    display: "flex",
                                    width: "200%",
                                    transform: `translateX(${isConfirm ? "-50%" : "0%"})`,
                                    transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                            >
                                <div style={{ width: "50%", flexShrink: 0, padding: 24, boxSizing: "border-box", maxHeight: "82vh", overflowY: "auto" }}>
                                    <BookingDetailsBody {...detailsProps} />
                                </div>
                                <div style={{ width: "50%", flexShrink: 0, padding: 24, boxSizing: "border-box", maxHeight: "82vh", overflowY: "auto" }}>
                                    <CancelBookingBody {...confirmProps} />
                                </div>
                            </div>
                        </PopupBody>
                    </PopupWrapper>
                );
            })()}

            {/* Slide-to-create + recurrence popup */}
            <CreateBookingPopup
                open={createOpen}
                selection={createSel}
                loading={creatingBooking}
                onClose={() => {
                    if (creatingBooking) return; // don't dismiss mid-request
                    setCreateOpen(false);
                    setCreateSel(null);
                }}
                onConfirm={handleCreateConfirm}
            />
            {/* Edit an under-review request — the create form, prefilled */}
            <CreateBookingPopup
                open={editOpen}
                mode="edit"
                selection={editSel}
                loading={savingBookingEdit}
                onClose={() => {
                    if (savingBookingEdit) return; // don't dismiss mid-request
                    setEditOpen(false);
                    setEditSel(null);
                }}
                onConfirm={handleEditConfirm}
            />
            {/* Repeat an existing booking */}
            <CreateBookingPopup
                open={recurOpen}
                recurOnly
                selection={
                    recurBooking
                        ? {
                            roomId: recurBooking.roomId,
                            roomName: roomNameFor(recurBooking.roomId),
                            date: recurBooking.date,
                            start: recurBooking.start,
                            end: recurBooking.end,
                            title: recurBooking.title,
                        }
                        : null
                }
                onClose={() => {
                    setRecurOpen(false);
                    setRecurBooking(null);
                }}
                onConfirm={handleRecurConfirm}
            />

            {/* Drag ghost */}
            {ghost && (
                <div
                    style={{
                        position: "fixed",
                        left: ghost.x + 12,
                        top: ghost.y + 12,
                        background: ghost.copy ? "rgba(34,150,83,0.95)" : "rgba(80,120,255,0.95)",
                        color: "#fff",
                        padding: "6px 10px",
                        borderRadius: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                        zIndex: 10000,
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {ghost.copy ? "Copy" : "Move"}: {ghost.label}
                </div>
            )}

            {/* Hover tooltip */}
            {tip && (
                <div
                    onMouseEnter={() => {
                        if (tipHideTimerRef.current) clearTimeout(tipHideTimerRef.current);
                    }}
                    onMouseLeave={() => setTip(null)}
                    style={{
                        position: "fixed",
                        left: tip.x,
                        top: tip.y,
                        transform:
                            tip.placement === "above"
                                ? "translate(-50%, -100%)"
                                : "translateX(-50%)",
                        background: globalTheme.colors.grey1,
                        color: globalTheme.colors.white1,
                        padding: "10px 12px",
                        borderRadius: 8,
                        boxShadow:
                            "0px 4px 4px 0px rgba(0,0,0,0.08), 0px 4px 6px -2px rgba(16,24,40,0.03)",
                        zIndex: 9999,
                        pointerEvents: "auto",
                        maxWidth: 260,
                        minWidth: 160,
                    }}
                >
                    <Flexbox column gap={6}>
                        <Flexbox spaceBW alignCenter gap={8}>
                            <Text font12 inter semiBold white style={{ wordBreak: "break-word" }}>
                                {tip.booking.title || "Booking"}
                            </Text>
                            {/* The request's ticket number, so an admin can quote
                                it straight from the hover. */}
                            {tip.booking.ticketId && (
                                <Text font11 inter semiBold white style={{ flexShrink: 0, opacity: 0.9 }}>
                                    #{tip.booking.ticketId}
                                </Text>
                            )}
                            {/* {tip.booking.pending && (
                                <span
                                    style={{
                                        flexShrink: 0,
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: "#3a2a08",
                                        background: DASH_PENDING,
                                        borderRadius: 10,
                                        padding: "2px 7px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Under Review
                                </span>
                            )} */}
                        </Flexbox>
                        <Flexbox gap={6} alignCenter maxWidth="max-content">
                            <Clock />
                            <Text font11 inter medium white>
                                {formatTime(tip.booking.start)} - {formatTime(tip.booking.end)}
                                {" · "}
                                {formatDuration(tip.booking.start, tip.booking.end)}
                            </Text>
                        </Flexbox>
                        {tip.room?.name && (
                            <Text font11 inter regular white style={{ opacity: 0.85 }}>
                                Room: {tip.room.name}
                            </Text>
                        )}
                        {tip.booking.bookedBy && (
                            <Text font11 inter regular white style={{ opacity: 0.85 }}>
                                Booked by: {tip.booking.bookedBy}
                            </Text>
                        )}
                        {tip.booking.schoolName && (
                            <Text font11 inter regular white style={{ opacity: 0.85 }}>
                                School: {tip.booking.schoolName}
                            </Text>
                        )}
                        <Text font11 inter regular white style={{ opacity: 0.85 }}>
                            Event: {tip.booking.isEvent ? "Yes" : "No"}
                        </Text>
                        {/* Admin colour tagging, inline. The details popup has the
                            same control, but it only opens on a confirmed booking
                            via double-click — this lets an admin retype any block
                            (including a pending one) straight from the hover. */}
                        {typeof onAssignColorType === "function" &&
                            tip.booking.requestId &&
                            colorTypes.length > 0 && (
                                <Flexbox column gap={5} alignStart style={{ marginTop: 4 }}>
                                    <Text font10 inter medium white style={{ opacity: 0.7 }}>
                                        Booking type
                                    </Text>
                                    <Flexbox gap={6} wrap alignCenter>
                                        {colorTypes.map((t) => {
                                            const selected = t.id === tip.booking.colorTypeId;
                                            return (
                                                <span
                                                    key={t.id}
                                                    title={t.name}
                                                    className="pointer"
                                                    onClick={() =>
                                                        onAssignColorType(
                                                            tip.booking.requestId,
                                                            selected ? null : t.id
                                                        )
                                                    }
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: "50%",
                                                        background: t.color,
                                                        flexShrink: 0,
                                                        boxShadow: selected
                                                            ? `0 0 0 2px ${globalTheme.colors.grey1}, 0 0 0 4px ${t.color}`
                                                            : "none",
                                                    }}
                                                />
                                            );
                                        })}
                                        {tip.booking.colorTypeId && (
                                            <Text
                                                font10
                                                inter
                                                medium
                                                white
                                                className="pointer"
                                                style={{ opacity: 0.75, textDecoration: "underline" }}
                                                onClick={() =>
                                                    onAssignColorType(tip.booking.requestId, null)
                                                }
                                            >
                                                Clear
                                            </Text>
                                        )}
                                    </Flexbox>
                                </Flexbox>
                            )}
                        {/* Under-review requests can still be edited, so the
                            hover carries the way in — the details popup has the
                            same button, but it takes a double-click to reach. */}
                        {canEditDetails(tip.booking) && (
                            <button
                                type="button"
                                onClick={() => openEditDetails(tip.booking)}
                                style={{
                                    marginTop: 6,
                                    width: "100%",
                                    padding: "7px 10px",
                                    borderRadius: 6,
                                    border: `1px solid ${globalTheme.colors.white1}`,
                                    cursor: "pointer",
                                    background: "transparent",
                                    color: globalTheme.colors.white1,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    fontFamily: "inherit",
                                }}
                            >
                                Edit Details
                            </button>
                        )}
                        {/* Day view only: the tooltip is the discoverable way in
                            to the drag handles now that double-click opens the
                            details preview instead. */}
                        {!isWeek && canMoveBooking(tip.booking) && (
                            <button
                                type="button"
                                onClick={() => beginResizeMode(tip.booking)}
                                style={{
                                    marginTop: 6,
                                    width: "100%",
                                    padding: "7px 10px",
                                    borderRadius: 6,
                                    border: `1px solid ${globalTheme.colors.white1}`,
                                    cursor: "pointer",
                                    background: "transparent",
                                    color: globalTheme.colors.white1,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    fontFamily: "inherit",
                                }}
                            >
                                Resize (R)
                            </button>
                        )}
                        {typeof onViewRequests === "function" && (
                            <button
                                type="button"
                                onClick={() => {
                                    onViewRequests({
                                        roomId: tip.booking.roomId,
                                        roomName: tip.room?.name,
                                        date: tip.booking.date,
                                        start: tip.booking.start,
                                        end: tip.booking.end,
                                    });
                                    setTip(null);
                                }}
                                style={{
                                    marginTop: 6,
                                    width: "100%",
                                    padding: "7px 10px",
                                    borderRadius: 6,
                                    border: "none",
                                    cursor: "pointer",
                                    background: tip.booking.pending ? DASH_PENDING : globalTheme.colors.white1,
                                    color: tip.booking.pending ? "#3a2a08" : globalTheme.colors.black3,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    fontFamily: "inherit",
                                }}
                            >
                                View Requests
                            </button>
                        )}
                    </Flexbox>
                </div>
            )}
        </WrapperBox>
    );
};

export default BookingCalendar;
